/**
 * Custom Icon Components
 * Centralized SVG icons for the Rituality platform.
 * Where possible, use lucide-react icons instead of these custom ones.
 */

import type React from "react"
import type { IconProps } from "./IconProps"

// ============================================================================
// EXISTING ICONS (already in file)
// ============================================================================

/**
 * Chat Bubble Icon - Used in IconButtons for navigation
 * Note: This uses stroke instead of fill for white color
 */
export const ChatBubbleIcon: React.FC<IconProps> = ({
  color = "white",
  size = 20,
  className,
  ...props
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 -2 20 24"
    fill="none"
    stroke={color}
    {...props}
  >
    <path
      d="M10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C8.38171 20 6.81782 19.6146 5.41286 18.888L1.58704 19.9553C0.922123 20.141 0.232581 19.7525 0.0469058 19.0876C-0.0145358 18.8676 -0.014506 18.6349 0.046948 18.4151L1.11461 14.5922C0.386366 13.186 0 11.6203 0 10C0 4.47715 4.47715 0 10 0ZM10 1.5C5.30558 1.5 1.5 5.30558 1.5 10C1.5 11.4696 1.87277 12.8834 2.57303 14.1375L2.72368 14.4072L1.61096 18.3914L5.59755 17.2792L5.86709 17.4295C7.12006 18.1281 8.53218 18.5 10 18.5C14.6944 18.5 18.5 14.6944 18.5 10C18.5 5.30558 14.6944 1.5 10 1.5ZM6.75 11H11.2483C11.6625 11 11.9983 11.3358 11.9983 11.75C11.9983 12.1297 11.7161 12.4435 11.35 12.4932L11.2483 12.5H6.75C6.33579 12.5 6 12.1642 6 11.75C6 11.3703 6.28215 11.0565 6.64823 11.0068L6.75 11H11.2483H6.75ZM6.75 7.5H13.2545C13.6687 7.5 14.0045 7.83579 14.0045 8.25C14.0045 8.6297 13.7223 8.94349 13.3563 8.99315L13.2545 9H6.75C6.33579 9 6 8.66421 6 8.25C6 7.8703 6.28215 7.55651 6.64823 7.50685L6.75 7.5H13.2545H6.75Z"
      fill="#F2F0EB"
      strokeWidth={0.1}
    />
  </svg>
)

export const BellIcon: React.FC<IconProps> = ({
  color = "white",
  size = 20,
  className,
  ...props
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 18 20"
    fill="none"
    stroke={color}
    {...props}
  >
    <path
      d="M8.97545 0C13.0254 0 16.3321 3.19474 16.4713 7.24905L16.4755 7.5V11.597L17.8555 14.753C17.9245 14.9109 17.9602 15.0814 17.9602 15.2538C17.9602 15.9441 17.4005 16.5038 16.7102 16.5038L11.9755 16.5053C11.9755 18.1621 10.6323 19.5053 8.97545 19.5053C7.37777 19.5053 6.07179 18.2564 5.98055 16.6815L5.975 16.503L1.25032 16.5038C1.07898 16.5038 0.909469 16.4686 0.752316 16.4003C0.119113 16.1252 -0.171235 15.389 0.103804 14.7558L1.47545 11.5979V7.49989C1.47605 3.3451 4.82754 0 8.97545 0ZM10.475 16.503L7.47545 16.5053C7.47545 17.3337 8.14703 18.0053 8.97545 18.0053C9.75515 18.0053 10.3959 17.4104 10.4686 16.6497L10.475 16.503ZM8.97545 1.5C5.65529 1.5 2.97593 4.17425 2.97545 7.5V11.9096L1.63147 15.0038H16.328L14.9755 11.9106L14.9756 7.51286L14.9718 7.28765C14.8608 4.05418 12.217 1.5 8.97545 1.5Z"
      fill="#F2F0EB"
      strokeWidth={0.1}
    />
  </svg>
)

export const SearchIcon: React.FC<IconProps> = ({
  color = "white",
  size = 20,
  className,
  ...props
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 18 20"
    fill="none"
    stroke={color}
    {...props}
  >
    <path
      d="M7.25 0C11.2541 0 14.5 3.24594 14.5 7.25C14.5 8.98187 13.8927 10.5719 12.8795 11.8188L17.7803 16.7197C18.0732 17.0126 18.0732 17.4874 17.7803 17.7803C17.5141 18.0466 17.0974 18.0708 16.8038 17.8529L16.7197 17.7803L11.8188 12.8795C10.5719 13.8927 8.98187 14.5 7.25 14.5C3.24594 14.5 0 11.2541 0 7.25C0 3.24594 3.24594 0 7.25 0ZM7.25 1.5C4.07436 1.5 1.5 4.07436 1.5 7.25C1.5 10.4256 4.07436 13 7.25 13C10.4256 13 13 10.4256 13 7.25C13 4.07436 10.4256 1.5 7.25 1.5Z"
      fill={color}
      strokeWidth={0.1}
    />
  </svg>
)
// ... (keeping existing icons: XCircleIcon, CheckCircleIcon, HeartFilledIcon, HeartOutlineIcon, UserProfileIcon, ShieldCheckIcon, WarningIcon, UserMinusIcon, CrownIcon)

// ============================================================================
// NEW ICONS - ADDED FOR CENTRALIZATION
// ============================================================================

/**
 * Play Icon - For video playback
 * Used in mediaPreview.tsx
 */
export const PlayIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 16,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M6.33973 3.84623C5.92326 3.61962 5.41602 3.92109 5.41602 4.39522V15.6055C5.41602 16.0796 5.92326 16.3811 6.33973 16.1545L16.641 10.5493C17.0761 10.3126 17.0761 9.68807 16.641 9.45136L6.33973 3.84623ZM4.16602 4.39522C4.16602 2.97284 5.68776 2.06842 6.93716 2.74824L17.2385 8.35337C18.5436 9.06351 18.5436 10.9372 17.2385 11.6473L6.93717 17.2525C5.68776 17.9323 4.16602 17.0279 4.16602 15.6055V4.39522Z"
      fill={color}
    />
  </svg>
)

/**
 * Check Icon - For completed course modules
 */
export const CheckIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 15,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 15 11"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M4.58333 8.82149L1.42259 5.66074C1.09715 5.33531 0.569515 5.33531 0.244078 5.66074C-0.0813592 5.98618 -0.0813592 6.51382 0.244078 6.83926L3.99408 10.5893C4.31951 10.9147 4.84715 10.9147 5.17259 10.5893L14.3393 1.42259C14.6647 1.09715 14.6647 0.569515 14.3393 0.244078C14.0138 -0.0813592 13.4862 -0.0813592 13.1607 0.244078L4.58333 8.82149Z"
      fill={color}
    />
  </svg>
)

/**
 * Video Camera Icon - For media preview placeholder
 * Used in mediaPreview.tsx
 */
export const VideoCameraIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 32,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className} {...props}>
    <path
      d="M16 6C10.48 6 6 10.48 6 16C6 21.52 10.48 26 16 26C21.52 26 26 21.52 26 16C26 10.48 21.52 6 16 6ZM14 20V12L22 16L14 20Z"
      fill={color}
    />
  </svg>
)

/**
 * Image Placeholder Icon - For image upload placeholder
 * Used in mediaPreview.tsx
 */
export const ImagePlaceholderIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 40,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" className={className} {...props}>
    <rect x="4" y="4" width="32" height="32" rx="4" stroke={color} strokeWidth="2" />
    <circle cx="16" cy="16" r="4" fill={color} opacity="0.3" />
    <path
      d="M4 28L12 20L18 26L26 18L36 28V32C36 34.2091 34.2091 36 32 36H8C5.79086 36 4 34.2091 4 32V28Z"
      fill={color}
      opacity="0.3"
    />
  </svg>
)

/**
 * Warning Triangle Icon - For error pages
 * Used in tooManyRequestsPage.tsx
 */
export const WarningTriangleIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 24,
  className,
  ...props
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    className={className}
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
)

/**
 * Star Icon - For community type indicator
 * Used in communityDetail.tsx
 */
export const StarIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 22,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 22 23" fill="none" className={className} {...props}>
    <path
      d="M4.16667 3.125C4.16667 2.26208 4.86623 1.5625 5.72924 1.5625C6.59225 1.5625 7.29182 2.26208 7.29182 3.125C7.29182 3.98792 6.59225 4.6875 5.72924 4.6875C4.86623 4.6875 4.16667 3.98792 4.16667 3.125ZM5.72924 0C4.00333 0 2.60417 1.39909 2.60417 3.125C2.60417 4.85091 4.00333 6.25 5.72924 6.25C7.45515 6.25 8.85432 4.85091 8.85432 3.125C8.85432 1.39909 7.45515 0 5.72924 0ZM6.77333e-08 9.13317C6.77333e-08 8.11613 0.824469 7.29167 1.8415 7.29167H6.80743C6.5353 7.76209 6.35251 8.29065 6.28224 8.85417H1.8415C1.68741 8.85417 1.5625 8.97908 1.5625 9.13317V9.50521C1.5625 9.65301 1.56521 10.5463 2.06942 11.3856C2.47463 12.06 3.26412 12.8016 4.93481 12.9805C4.43372 13.3136 4.04282 13.7993 3.82841 14.3713C2.26725 13.9897 1.29966 13.1383 0.730058 12.1903C-6.6186e-05 10.975 -9.18609e-06 9.72452 6.77333e-08 9.50752V9.13317ZM14.5512 8.85417C14.481 8.29065 14.2982 7.76209 14.026 7.29167H18.9918C20.0089 7.29167 20.8333 8.11613 20.8333 9.13317V9.50752C20.8333 9.70349 20.8334 10.7424 20.2956 11.837C19.883 11.5165 19.4322 11.2427 18.9514 11.0235C19.2688 10.3054 19.2708 9.63181 19.2708 9.50521V9.13317C19.2708 8.97908 19.1459 8.85417 18.9918 8.85417H14.5512ZM12.9361 11.2243C13.3169 10.7065 13.5418 10.067 13.5418 9.375C13.5418 7.64909 12.1426 6.25 10.4167 6.25C8.69083 6.25 7.29167 7.64909 7.29167 9.375C7.29167 11.1009 8.69083 12.5 10.4167 12.5C10.7696 12.5 11.1087 12.4415 11.4251 12.3337C12.0358 12.1257 12.5614 11.7339 12.9361 11.2243ZM10.4394 13.5417H6.529C5.51197 13.5417 4.6875 14.3661 4.6875 15.3832V15.7575C4.68749 15.9745 4.68743 17.225 5.41756 18.4403C6.18984 19.7257 7.69372 20.8333 10.4167 20.8333L10.4394 20.8333C10.1243 20.3411 9.87112 19.8055 9.69087 19.2374C7.97313 19.0718 7.16764 18.3192 6.75692 17.6356C6.2527 16.7963 6.25 15.903 6.25 15.7552V15.3832C6.25 15.2291 6.37491 15.1042 6.529 15.1042H9.70156C9.88108 14.5485 10.1305 14.0242 10.4394 13.5417ZM15.1042 1.5625C14.2412 1.5625 13.5417 2.26208 13.5417 3.125C13.5417 3.98792 14.2412 4.6875 15.1042 4.6875C15.9673 4.6875 16.6668 3.98792 16.6668 3.125C16.6668 2.26208 15.9673 1.5625 15.1042 1.5625ZM11.9792 3.125C11.9792 1.39909 13.3783 0 15.1042 0C16.8301 0 18.2293 1.39909 18.2293 3.125C18.2293 4.85091 16.8301 6.25 15.1042 6.25C13.3783 6.25 11.9792 4.85091 11.9792 3.125ZM8.85417 9.375C8.85417 8.51208 9.55373 7.8125 10.4167 7.8125C11.2798 7.8125 11.9793 8.51208 11.9793 9.375C11.9793 10.2379 11.2798 10.9375 10.4167 10.9375C9.55373 10.9375 8.85417 10.2379 8.85417 9.375ZM16.1458 11.4583C19.31 11.4583 21.875 14.0234 21.875 17.1875C21.875 20.3516 19.31 22.9167 16.1458 22.9167C12.9817 22.9167 10.4167 20.3516 10.4167 17.1875C10.4167 14.0234 12.9817 11.4583 16.1458 11.4583ZM16.6678 20.3162L16.6673 17.7083H19.2673C19.5546 17.7083 19.7876 17.4752 19.7876 17.1875C19.7876 16.8999 19.5546 16.6667 19.2673 16.6667H16.6671L16.6667 14.0617C16.6667 13.7741 16.4335 13.5409 16.1458 13.5409C15.8582 13.5409 15.625 13.7741 15.625 14.0617L15.6255 16.6667H13.0162C12.7289 16.6667 12.4959 16.8999 12.4959 17.1875C12.4959 17.4752 12.7289 17.7083 13.0162 17.7083H15.6257L15.6262 20.3162C15.6262 20.6038 15.8593 20.837 16.147 20.837C16.4346 20.837 16.6678 20.6038 16.6678 20.3162Z"
      fill={color}
    />
  </svg>
)

/**
 * Google Icon - For OAuth login
 * Used in ConnectedAccountsSection.tsx
 */
export const GoogleIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 24,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...props}>
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      className="fill-current"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      className="fill-current"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      className="fill-current"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      className="fill-current"
    />
  </svg>
)

/**
 * Google Icon - For OAuth login
 * Used in SocialLoginButtons.tsx
 */
export const GoogleLoginIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 24,
  className,
  ...props
}) => (
  <svg viewBox="0 0 18 18" className={className} aria-hidden="true" {...props}>
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M17.64 9.20419C17.64 8.56601 17.5827 7.95237 17.4764 7.36328H9V10.8446H13.8436C13.635 11.9696 13.0009 12.9228 12.0477 13.561V15.8192H14.9564C16.6582 14.2524 17.64 11.9451 17.64 9.20419Z"
      fill="#4285F4"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z"
      fill="#34A853"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M3.96409 10.7098C3.78409 10.1698 3.68182 9.59301 3.68182 8.99983C3.68182 8.40664 3.78409 7.82983 3.96409 7.28983V4.95801H0.957273C0.347727 6.17301 0 7.54755 0 8.99983C0 10.4521 0.347727 11.8266 0.957273 13.0416L3.96409 10.7098Z"
      fill="#FBBC05"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
      fill="#EA4335"
    />
  </svg>
)

/**
 * Apple Icon - For OAuth login
 * Used in SocialLoginButtons.tsx, ConnectedAccountsSection.tsx
 */
export const AppleIcon: React.FC<IconProps> = ({
  color = "#ffffff",
  size = 24,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill={color} {...props}>
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
)

/**
 * Facebook Icon - For social sharing
 * Used in ConnectedAccountsSection.tsx, CommunitySuccessModal.tsx
 */
export const FacebookIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 24,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...props}>
    <path
      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      className="fill-current"
    />
  </svg>
)

/**
 * Instagram Icon - For social sharing
 * Used in ConnectedAccountsSection.tsx, CommunitySuccessModal.tsx
 */
export const InstagramIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...props}>
    <path
      className="fill-current"
      d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
    />
  </svg>
)

/**
 * Image Upload Icon - For profile image upload
 * Used in ProfileImageUpload.tsx
 */
export const ImageUploadIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 50,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 50 50" className={className} {...props}>
    <path
      xmlns="http://www.w3.org/2000/svg"
      d="M36.4583 25.0002C42.7866 25.0002 47.9166 30.1302 47.9166 36.4585C47.9166 42.7868 42.7866 47.9168 36.4583 47.9168C30.13 47.9168 25 42.7868 25 36.4585C25 30.1302 30.13 25.0002 36.4583 25.0002ZM36.4583 29.1652L36.271 29.182C35.8459 29.2592 35.5106 29.5945 35.4334 30.0197L35.4166 30.2069L35.4159 35.4152L30.2034 35.4168L30.0162 35.4336C29.591 35.5108 29.2557 35.8461 29.1785 36.2713L29.1617 36.4585L29.1785 36.6457C29.2557 37.0709 29.591 37.4062 30.0162 37.4834L30.2034 37.5002L35.418 37.4986L35.4189 42.7158L35.4357 42.903C35.5129 43.3282 35.8482 43.6635 36.2734 43.7406L36.4606 43.7574L36.6478 43.7406C37.073 43.6635 37.4083 43.3282 37.4855 42.903L37.5023 42.7158L37.5013 37.4986L42.7178 37.5002L42.905 37.4834C43.3302 37.4062 43.6655 37.0709 43.7427 36.6457L43.7595 36.4585L43.7427 36.2713C43.6655 35.8461 43.3302 35.5108 42.905 35.4336L42.7178 35.4168L37.4992 35.4152L37.5 30.2069L37.4832 30.0197C37.406 29.5945 37.0707 29.2592 36.6455 29.182L36.4583 29.1652ZM29.0098 5.21484C30.6719 5.21484 32.2097 6.095 33.0515 7.52812L34.7484 10.4168H39.0625C42.8019 10.4168 45.8333 13.4482 45.8333 17.1877L45.835 26.6884C44.9096 25.8001 43.8583 25.0419 42.7105 24.4434L42.7083 17.1877C42.7083 15.1741 41.076 13.5418 39.0625 13.5418H33.8541C33.3001 13.5418 32.7875 13.2484 32.5069 12.7707L30.357 9.11093C30.0764 8.63323 29.5638 8.33984 29.0098 8.33984H21.0882C20.6227 8.33984 20.1858 8.54707 19.8915 8.89774L19.7567 9.08474L17.4773 12.7969C17.1931 13.2598 16.6889 13.5418 16.1458 13.5418H10.9375C8.92392 13.5418 7.29163 15.1741 7.29163 17.1877V36.9793C7.29163 38.9929 8.92392 40.6252 10.9375 40.6252L23.5694 40.624C23.9285 41.736 24.4275 42.7851 25.0457 43.7506L10.9375 43.7502C7.19803 43.7502 4.16663 40.7188 4.16663 36.9793V17.1877C4.16663 13.4482 7.19803 10.4168 10.9375 10.4168H15.2717L17.0937 7.44954C17.9463 6.061 19.4588 5.21484 21.0882 5.21484H29.0098ZM25 16.6668C29.1703 16.6668 32.7047 19.3898 33.922 23.1549C32.8793 23.3516 31.8796 23.6684 30.9369 24.0899C30.1189 21.5937 27.7699 19.7918 25 19.7918C21.5482 19.7918 18.75 22.59 18.75 26.0418C18.75 29.0197 20.8326 31.5111 23.6207 32.1391C23.2928 33.1114 23.0725 34.1366 22.9746 35.1973C18.7707 34.2719 15.625 30.5242 15.625 26.0418C15.625 20.8642 19.8223 16.6668 25 16.6668Z"
      fill={color}
    />
  </svg>
)

/**
 * Plus Icon - For adding photos
 * Used in profilePhotosSection.tsx
 */
export const PlusIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 48,
  className,
  ...props
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    className={className}
    aria-hidden="true"
    {...props}
  >
    <rect x="8" y="8" width="32" height="32" rx="4" stroke={color} strokeWidth="2" />
    <path d="M24 16V32M16 24H32" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
)

/**
 * Rituals Icon - For create modal (ritual option)
 * Used in CreateModal.tsx
 */
export const RitualsIcon: React.FC<IconProps> = ({
  color = "#C9A876",
  size = 25,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 19 19" fill="none" className={className} {...props}>
    <path
      d="M9.375 4.16667C9.80647 4.16667 10.1562 4.51644 10.1562 4.94792V8.59375H13.8021C14.2336 8.59375 14.5833 8.94353 14.5833 9.375C14.5833 9.80647 14.2336 10.1562 13.8021 10.1562H10.1562V13.8021C10.1562 14.2336 9.80647 14.5833 9.375 14.5833C8.94353 14.5833 8.59375 14.2336 8.59375 13.8021V10.1562H4.94792C4.51644 10.1562 4.16667 9.80647 4.16667 9.375C4.16667 8.94353 4.51644 8.59375 4.94792 8.59375H8.59375V4.94792C8.59375 4.51644 8.94353 4.16667 9.375 4.16667ZM0 3.38542C0 1.5157 1.5157 0 3.38542 0H15.3646C17.2343 0 18.75 1.5157 18.75 3.38542V15.3646C18.75 17.2343 17.2343 18.75 15.3646 18.75H3.38542C1.5157 18.75 0 17.2343 0 15.3646V3.38542ZM3.38542 1.5625C2.37865 1.5625 1.5625 2.37865 1.5625 3.38542V15.3646C1.5625 16.3714 2.37865 17.1875 3.38542 17.1875H15.3646C16.3714 17.1875 17.1875 16.3714 17.1875 15.3646V3.38542C17.1875 2.37865 16.3714 1.5625 15.3646 1.5625H3.38542Z"
      fill={color}
    />
  </svg>
)

/**
 * Coaching Icon - For create modal (coaching option)
 * Used in CreateModal.tsx
 */
export const CoachingIcon: React.FC<IconProps> = ({
  color = "#B8B896",
  size = 25,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 18 21" fill="none" className={className} {...props}>
    <path
      d="M3.125 4.16667C3.125 3.59137 3.59137 3.125 4.16667 3.125H12.5C13.0753 3.125 13.5417 3.59137 13.5417 4.16667V6.25C13.5417 6.8253 13.0753 7.29167 12.5 7.29167H4.16667C3.59137 7.29167 3.125 6.8253 3.125 6.25V4.16667ZM4.6875 5.72917H11.9792V4.6875H4.6875V5.72917ZM0 2.60417C0 1.16593 1.16593 0 2.60417 0H14.5833C16.0216 0 17.1875 1.16593 17.1875 2.60417V17.4479C17.1875 17.8794 16.8377 18.2292 16.4062 18.2292H1.5625C1.5625 18.8045 2.02887 19.2708 2.60417 19.2708H16.4062C16.8377 19.2708 17.1875 19.6206 17.1875 20.0521C17.1875 20.4836 16.8377 20.8333 16.4062 20.8333H2.60417C1.16593 20.8333 0 19.6674 0 18.2292V2.60417ZM1.5625 16.6667H15.625V2.60417C15.625 2.02887 15.1586 1.5625 14.5833 1.5625H2.60417C2.02887 1.5625 1.5625 2.02887 1.5625 2.60417V16.6667Z"
      fill={color}
    />
  </svg>
)

/**
 * Community Icon - For create modal (community option)
 * Used in CreateModal.tsx
 */
export const CommunityCreateIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 30,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M5.72924 1.5625C4.86623 1.5625 4.16667 2.26208 4.16667 3.125C4.16667 3.98792 4.86623 4.6875 5.72924 4.6875C6.59225 4.6875 7.29182 3.98792 7.29182 3.125C7.29182 2.26208 6.59225 1.5625 5.72924 1.5625ZM2.60417 3.125C2.60417 1.39909 4.00333 0 5.72924 0C7.45515 0 8.85432 1.39909 8.85432 3.125C8.85432 4.85091 7.45515 6.25 5.72924 6.25C4.00333 6.25 2.60417 4.85091 2.60417 3.125ZM1.8415 7.29167C0.824468 7.29167 7.07683e-08 8.11614 7.07683e-08 9.13317V9.50752C-9.46117e-06 9.72452 -6.64343e-05 10.975 0.730057 12.1903C1.29966 13.1384 2.26725 13.9897 3.82841 14.3713C4.04282 13.7993 4.43372 13.3136 4.93482 12.9805C3.26412 12.8016 2.47463 12.06 2.06942 11.3856C1.56521 10.5463 1.5625 9.65301 1.5625 9.50521V9.13317C1.5625 8.97908 1.68741 8.85417 1.8415 8.85417H6.28224C6.35251 8.29065 6.53531 7.76209 6.80743 7.29167H1.8415ZM14.026 7.29167C14.2982 7.76209 14.481 8.29065 14.5512 8.85417H18.9918C19.1459 8.85417 19.2708 8.97908 19.2708 9.13317V9.50521C19.2708 9.65301 19.2681 10.5463 18.7639 11.3856C18.3587 12.06 17.5692 12.8016 15.8985 12.9805C16.3996 13.3136 16.7905 13.7993 17.0049 14.3713C18.5661 13.9897 19.5337 13.1384 20.1033 12.1903C20.8334 10.975 20.8333 9.72452 20.8333 9.50752V9.13317C20.8333 8.11614 20.0089 7.29167 18.9918 7.29167H14.026ZM13.5417 3.125C13.5417 2.26208 14.2412 1.5625 15.1042 1.5625C15.9673 1.5625 16.6668 2.26208 16.6668 3.125C16.6668 3.98792 15.9673 4.6875 15.1042 4.6875C14.2412 4.6875 13.5417 3.98792 13.5417 3.125ZM15.1042 0C13.3783 0 11.9792 1.39909 11.9792 3.125C11.9792 4.85091 13.3783 6.25 15.1042 6.25C16.8301 6.25 18.2293 4.85091 18.2293 3.125C18.2293 1.39909 16.8301 0 15.1042 0ZM10.4167 7.8125C9.55373 7.8125 8.85417 8.51208 8.85417 9.375C8.85417 10.2379 9.55373 10.9375 10.4167 10.9375C11.2798 10.9375 11.9793 10.2379 11.9793 9.375C11.9793 8.51208 11.2798 7.8125 10.4167 7.8125ZM7.29167 9.375C7.29167 7.64909 8.69083 6.25 10.4167 6.25C12.1426 6.25 13.5418 7.64909 13.5418 9.375C13.5418 11.1009 12.1426 12.5 10.4167 12.5C8.69083 12.5 7.29167 11.1009 7.29167 9.375ZM4.6875 15.3832C4.6875 14.3661 5.51197 13.5417 6.529 13.5417H14.3043C15.3214 13.5417 16.1458 14.3661 16.1458 15.3832V15.7575C16.1458 15.9745 16.1459 17.225 15.4158 18.4403C14.6435 19.7257 13.1396 20.8333 10.4167 20.8333C7.69372 20.8333 6.18984 19.7257 5.41756 18.4403C4.68743 17.225 4.68749 15.9745 4.6875 15.7575V15.3832ZM6.529 15.1042C6.37491 15.1042 6.25 15.2291 6.25 15.3832V15.7552C6.25 15.903 6.25271 16.7963 6.75692 17.6356C7.22162 18.4091 8.1917 19.2708 10.4167 19.2708C12.6416 19.2708 13.6117 18.4091 14.0764 17.6356C14.5806 16.7963 14.5833 15.903 14.5833 15.7552V15.3832C14.5833 15.2291 14.4584 15.1042 14.3043 15.1042H6.529Z"
      fill={color}
    />
  </svg>
)

/**
 * TikTok Icon - For social sharing
 * Used in CommunitySuccessModal.tsx
 */
export const TikTokIcon: React.FC<IconProps> = ({
  color = "white",
  size = 14,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 14 16" fill="none" className={className} {...props}>
    <path
      d="M10.0484 0H7.35187V10.8985C7.35187 12.1971 6.31479 13.2638 5.02418 13.2638C3.73356 13.2638 2.69646 12.1971 2.69646 10.8985C2.69646 9.6232 3.71051 8.57969 4.95505 8.53333V5.79711C2.21248 5.84347 0 8.09276 0 10.8985C0 13.7276 2.25857 16 5.04723 16C7.83585 16 10.0944 13.7044 10.0944 10.8985V5.31013C11.1085 6.05218 12.353 6.49276 13.6667 6.51596V3.77971C11.6386 3.71015 10.0484 2.04058 10.0484 0Z"
      fill={color}
    />
  </svg>
)

/**
 * Threads Icon - For social sharing
 * Used in CommunitySuccessModal.tsx
 */
export const ThreadsIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 15,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 15 18" fill="none" className={className} {...props}>
    <path
      d="M11.6722 8.34265C11.5971 8.30549 11.5208 8.26972 11.4435 8.23548C11.3089 5.67546 9.95399 4.20984 7.67888 4.19484C7.66858 4.19478 7.65832 4.19478 7.64802 4.19478C6.28721 4.19478 5.15544 4.79446 4.45885 5.88569L5.71009 6.77183C6.23047 5.95672 7.04716 5.78295 7.64862 5.78295C7.65556 5.78295 7.66254 5.78295 7.66941 5.78302C8.41852 5.78795 8.9838 6.01281 9.34966 6.45131C9.61591 6.77056 9.79399 7.21172 9.88216 7.76848C9.21799 7.65194 8.4997 7.61611 7.73183 7.66156C5.56876 7.79019 4.17817 9.09262 4.27156 10.9024C4.31896 11.8204 4.76194 12.6101 5.51885 13.126C6.1588 13.5622 6.98303 13.7754 7.83965 13.7272C8.97091 13.6631 9.85837 13.2175 10.4775 12.4028C10.9477 11.784 11.2451 10.9822 11.3764 9.97181C11.9155 10.3077 12.3151 10.7498 12.5357 11.2811C12.911 12.1844 12.9329 13.6687 11.7597 14.8789C10.7319 15.939 9.49632 16.3976 7.62908 16.4118C5.55782 16.3959 3.99135 15.7102 2.97288 14.3735C2.01916 13.1218 1.52627 11.3139 1.50788 9C1.52627 6.68604 2.01916 4.87815 2.97288 3.6265C3.99135 2.28983 5.55779 1.60406 7.62906 1.58817C9.71535 1.60418 11.3091 2.29326 12.3666 3.63637C12.8851 4.29502 13.276 5.12333 13.5337 6.08909L15 5.68521C14.6876 4.49646 14.1961 3.4721 13.5272 2.62256C12.1715 0.900626 10.1888 0.0182951 7.63417 0H7.62394C5.07449 0.0182316 3.11399 0.903919 1.79693 2.63243C0.62493 4.1706 0.0203773 6.31085 6.35652e-05 8.99367L0 9L6.35652e-05 9.00633C0.0203773 11.6891 0.62493 13.8294 1.79693 15.3676C3.11399 17.0961 5.07449 17.9818 7.62394 18H7.63417C9.90078 17.9838 11.4984 17.3711 12.8146 16.0135C14.5366 14.2374 14.4848 12.0112 13.9172 10.6445C13.51 9.66441 12.7337 8.86839 11.6722 8.34265ZM7.75867 12.1413C6.81063 12.1964 5.82572 11.7571 5.77716 10.8161C5.74116 10.1184 6.25809 9.33994 7.81685 9.2472C7.99536 9.23657 8.17053 9.23137 8.34261 9.23137C8.90879 9.23137 9.43847 9.28816 9.92003 9.39684C9.74041 11.7127 8.68686 12.0887 7.75867 12.1413Z"
      fill={color}
    />
  </svg>
)

/**
 * Link/Share Icon - For copying link
 * Used in CommunitySuccessModal.tsx, communityInfoSection.tsx
 */
export const LinkShareIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 15,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 15 8" fill="none" className={className} {...props}>
    <path
      d="M5.25 0C5.66421 0 6 0.335786 6 0.75C6 1.13463 5.71047 1.45163 5.33747 1.49495L5.25 1.5H3.75C2.50736 1.5 1.5 2.50736 1.5 3.75C1.5 4.94826 2.43669 5.92775 3.6178 5.99618L3.75 6H5.25C5.66421 6 6 6.33579 6 6.75C6 7.13463 5.71047 7.45163 5.33747 7.49495L5.25 7.5H3.75C1.67893 7.5 0 5.82107 0 3.75C0 1.73343 1.59173 0.0886365 3.58733 0.0034647L3.75 0H5.25ZM11.25 0C13.3211 0 15 1.67893 15 3.75C15 5.76657 13.4083 7.41136 11.4127 7.49654L11.25 7.5H9.75C9.33579 7.5 9 7.16421 9 6.75C9 6.36537 9.28953 6.04837 9.66253 6.00505L9.75 6H11.25C12.4926 6 13.5 4.99264 13.5 3.75C13.5 2.55174 12.5633 1.57225 11.3822 1.50382L11.25 1.5H9.75C9.33579 1.5 9 1.16421 9 0.75C9 0.365373 9.28953 0.0483696 9.66253 0.00504577L9.75 0H11.25ZM3.75 3H11.25C11.6642 3 12 3.33579 12 3.75C12 4.13463 11.7105 4.45163 11.3375 4.49495L11.25 4.5H3.75C3.33579 4.5 3 4.16421 3 3.75C3 3.36537 3.28953 3.04837 3.66253 3.00505L3.75 3H11.25H3.75Z"
      fill={color}
    />
  </svg>
)

/**
 * Checkmark Icon - For completion indicators
 * Used in ProfileIncompleteModal.tsx
 */
export const CheckmarkIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    viewBox="0 0 20 20"
    fill="currentColor"
    {...props}
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
)

/**
 * Settings Icon - For settings section
 * Used in communityInfoSection.tsx
 */
export const SettingsIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 24,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...props}>
    <path
      d="M12 15a3 3 0 100-6 3 3 0 000 6z"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"
    />
  </svg>
)

/**
 * Leave Community Icon - For leaving community
 * Used in communityInfoSection.tsx
 */
export const LeaveCommunityIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 20,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 20 11" fill="none" className={className} {...props}>
    <path
      d="M11.75 6.25C12.1642 6.25 12.5 5.91421 12.5 5.5C12.5 5.08579 12.1642 4.75 11.75 4.75L2.49365 4.75L5.79446 1.26581C6.07934 0.96511 6.06651 0.49041 5.76581 0.205537C5.46511 -0.0793362 4.99041 -0.0665064 4.70554 0.234193L0.205536 4.98419C-0.0685117 5.27347 -0.0685117 5.72654 0.205536 6.01581L4.70554 10.7658C4.99041 11.0665 5.46511 11.0793 5.76581 10.7945C6.06651 10.5096 6.07934 10.0349 5.79447 9.73419L2.49365 6.25L11.75 6.25ZM17 8.5C18.6569 8.5 20 7.15685 20 5.5C20 3.84314 18.6569 2.5 17 2.5C15.3431 2.5 14 3.84315 14 5.5C14 7.15685 15.3431 8.5 17 8.5ZM17 4C17.8284 4 18.5 4.67157 18.5 5.5C18.5 6.32843 17.8284 7 17 7C16.1716 7 15.5 6.32843 15.5 5.5C15.5 4.67157 16.1716 4 17 4Z"
      fill={color}
    />
  </svg>
)

/**
 * X Icon (reject/cancel) - Circle with X inside
 * Used in membersTab for reject request
 */
export const XCircleIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 25,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 25 25" fill="none" className={className} {...props}>
    <path
      d="M12.5 0C19.4036 0 25 5.59644 25 12.5C25 19.4036 19.4036 25 12.5 25C5.59644 25 0 19.4036 0 12.5C0 5.59644 5.59644 0 12.5 0ZM12.5 1.875C6.63197 1.875 1.875 6.63197 1.875 12.5C1.875 18.368 6.63197 23.125 12.5 23.125C18.368 23.125 23.125 18.368 23.125 12.5C23.125 6.63197 18.368 1.875 12.5 1.875ZM16.8078 7.99632L16.9129 8.08709C17.2457 8.41992 17.276 8.94075 17.0037 9.30777L16.9129 9.41291L13.8263 12.5L16.9129 15.5871C17.2457 15.9199 17.276 16.4408 17.0037 16.8078L16.9129 16.9129C16.5801 17.2457 16.0592 17.276 15.6922 17.0037L15.5871 16.9129L12.5 13.8263L9.41291 16.9129C9.08008 17.2457 8.55925 17.276 8.19223 17.0037L8.08709 16.9129C7.75425 16.5801 7.724 16.0592 7.99632 15.6922L8.08709 15.5871L11.1737 12.5L8.08709 9.41291C7.75425 9.08008 7.724 8.55925 7.99632 8.19223L8.08709 8.08709C8.41992 7.75425 8.94075 7.724 9.30777 7.99632L9.41291 8.08709L12.5 11.1737L15.5871 8.08709C15.9199 7.75425 16.4408 7.724 16.8078 7.99632Z"
      fill={color}
    />
  </svg>
)

/**
 * Check Icon (accept) - Circle with checkmark inside
 * Used in membersTab for accept request
 */
export const CheckCircleIcon: React.FC<IconProps> = ({
  color = "#5A5B3F",
  size = 25,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 25 25" fill="none" className={className} {...props}>
    <path
      d="M12.5 0C19.4036 0 25 5.59644 25 12.5C25 19.4036 19.4036 25 12.5 25C5.59644 25 0 19.4036 0 12.5C0 5.59644 5.59644 0 12.5 0ZM12.5 1.875C6.63197 1.875 1.875 6.63197 1.875 12.5C1.875 18.368 6.63197 23.125 12.5 23.125C18.368 23.125 23.125 18.368 23.125 12.5C23.125 6.63197 18.368 1.875 12.5 1.875ZM10.9375 14.2992L16.5246 8.71209C16.8907 8.34597 17.4843 8.34597 17.8504 8.71209C18.1832 9.04492 18.2135 9.56575 17.9412 9.93277L17.8504 10.0379L11.6004 16.2879C11.2676 16.6207 10.7467 16.651 10.3797 16.3787L10.2746 16.2879L7.14959 13.1629C6.78347 12.7968 6.78347 12.2032 7.14959 11.8371C7.48242 11.5043 8.00325 11.474 8.37027 11.7463L8.47541 11.8371L10.9375 14.2992L16.5246 8.71209L10.9375 14.2992Z"
      fill={color}
    />
  </svg>
)

/**
 * Heart Icon - Filled heart for "following" state
 * Used in membersTab for follow button (filled state)
 */
export const HeartFilledIcon: React.FC<IconProps> = ({
  color = "#5A5B3F",
  size = 21,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M15.4965 9.99538C18.534 9.99538 20.9965 12.4578 20.9965 15.4954C20.9965 18.5329 18.534 20.9954 15.4965 20.9954C12.4589 20.9954 9.99646 18.5329 9.99646 15.4954C9.99646 12.4578 12.4589 9.99538 15.4965 9.99538ZM10.0187 11.9947C9.37163 13.0051 8.99646 14.2064 8.99646 15.4954C8.99646 17.1257 9.59668 18.6158 10.5882 19.7569C9.79266 19.917 8.92778 19.9964 7.99646 19.9964C4.57549 19.9964 2.05253 18.9243 0.510368 16.7599C0.178405 16.294 0 15.7362 0 15.1641V14.2442C0 13.0022 1.00686 11.9953 2.24888 11.9953L10.0187 11.9947ZM12.85 15.1418C12.6547 14.9466 12.3382 14.9466 12.1429 15.1418C11.9476 15.3371 11.9476 15.6537 12.1429 15.8489L14.1429 17.8489C14.3382 18.0442 14.6547 18.0442 14.85 17.8489L18.85 13.8489C19.0453 13.6537 19.0453 13.3371 18.85 13.1418C18.6547 12.9466 18.3382 12.9466 18.1429 13.1418L14.4965 16.7883L12.85 15.1418ZM7.99646 0C10.7579 0 12.9965 2.23858 12.9965 5C12.9965 7.76142 10.7579 10 7.99646 10C5.23503 10 2.99646 7.76142 2.99646 5C2.99646 2.23858 5.23503 0 7.99646 0Z"
      fill={color}
    />
  </svg>
)

/**
 * Heart Icon - Outlined heart for "not following" state
 * Used in membersTab for follow button (unfilled state)
 */
export const HeartOutlineIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 21,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M15.4965 9.99538C18.534 9.99538 20.9965 12.4578 20.9965 15.4954C20.9965 18.5329 18.534 20.9954 15.4965 20.9954C12.4589 20.9954 9.99646 18.5329 9.99646 15.4954C9.99646 12.4578 12.4589 9.99538 15.4965 9.99538ZM10.0187 11.9947C9.72202 12.458 9.48249 12.9614 9.31008 13.495L2.24888 13.4953C1.83528 13.4953 1.5 13.8306 1.5 14.2442V14.8219C1.5 15.3575 1.69111 15.8756 2.03897 16.283C3.29228 17.7507 5.25815 18.4964 7.99646 18.4964C8.5929 18.4964 9.15277 18.4611 9.67669 18.3905C9.92188 18.8854 10.2294 19.344 10.5882 19.7569C9.7927 19.9171 8.92782 19.9964 7.99646 19.9964C4.8506 19.9964 2.46458 19.0913 0.89828 17.257C0.318518 16.5781 0 15.7146 0 14.8219V14.2442C0 13.0022 1.00686 11.9953 2.24888 11.9953L10.0187 11.9947ZM15.4965 11.9954L15.4066 12.0034C15.2025 12.0405 15.0416 12.2014 15.0045 12.4055L14.9965 12.4954L14.9955 14.9954H12.5004L12.4105 15.0034C12.2064 15.0405 12.0455 15.2014 12.0084 15.4055L12.0004 15.4954L12.0084 15.5853C12.0455 15.7893 12.2064 15.9503 12.4105 15.9873L12.5004 15.9954H14.9955L14.9965 18.4954L15.0045 18.5852C15.0416 18.7893 15.2025 18.9503 15.4066 18.9873L15.4965 18.9954L15.5863 18.9873C15.7904 18.9503 15.9514 18.7893 15.9884 18.5852L15.9965 18.4954L15.9955 15.9954H18.5004L18.5902 15.9873C18.7943 15.9503 18.9553 15.7893 18.9923 15.5853L19.0004 15.4954L18.9923 15.4055C18.9553 15.2014 18.7943 15.0405 18.5902 15.0034L18.5004 14.9954H15.9955L15.9965 12.4954L15.9884 12.4055C15.9514 12.2014 15.7904 12.0405 15.5863 12.0034L15.4965 11.9954ZM7.99646 0C10.7579 0 12.9965 2.23858 12.9965 5C12.9965 7.76142 10.7579 10 7.99646 10C5.23503 10 2.99646 7.76142 2.99646 5C2.99646 2.23858 5.23503 0 7.99646 0ZM7.99646 1.5C6.06346 1.5 4.49646 3.067 4.49646 5C4.49646 6.933 6.06346 8.5 7.99646 8.5C9.92945 8.5 11.4965 6.933 11.4965 5C11.4965 3.067 9.92945 1.5 7.99646 1.5Z"
      fill={color}
    />
  </svg>
)

/**
 * Remove Account Icon - For remove account action in settings
 * Shows person with remove/exit symbol
 */
export const RemoveAccountIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 21,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M15.4965 9.99538C18.534 9.99538 20.9965 12.4578 20.9965 15.4954C20.9965 18.5329 18.534 20.9954 15.4965 20.9954C12.4589 20.9954 9.99646 18.5329 9.99646 15.4954C9.99646 12.4578 12.4589 9.99538 15.4965 9.99538ZM10.0187 11.9947C9.72202 12.458 9.48249 12.9614 9.31008 13.495L2.25 13.4953C1.83579 13.4953 1.5 13.8311 1.5 14.2453V15.1526C1.5 15.8083 1.78599 16.4313 2.28317 16.8587C3.54125 17.9404 5.43726 18.4964 7.99646 18.4964C8.5953 18.4964 9.1579 18.466 9.6846 18.4055C9.93351 18.9057 10.2473 19.3694 10.6136 19.7859C9.81137 19.9267 8.93842 19.9964 7.99646 19.9964C5.10696 19.9964 2.86814 19.3398 1.30528 17.9962C0.476655 17.2838 0 16.2454 0 15.1526V14.2453C0 13.0027 1.00736 11.9953 2.25 11.9953L10.0187 11.9947ZM13.0895 12.9617L13.0203 13.0195L12.9624 13.0888C12.8443 13.2593 12.8443 13.4869 12.9624 13.6574L13.0203 13.7266L14.7898 15.4961L13.0228 17.2629L12.965 17.3321C12.8469 17.5026 12.8469 17.7302 12.965 17.9007L13.0228 17.97L13.0921 18.0278C13.2626 18.146 13.4902 18.146 13.6607 18.0278L13.73 17.97L15.4968 16.2031L17.2659 17.9722L17.3351 18.0301C17.5056 18.1482 17.7332 18.1482 17.9037 18.0301L17.973 17.9722L18.0308 17.903C18.1489 17.7325 18.1489 17.5049 18.0308 17.3344L17.973 17.2651L16.2038 15.4961L17.9757 13.7267L18.0336 13.6574C18.1517 13.4869 18.1517 13.2593 18.0336 13.0888L17.9757 13.0196L17.9065 12.9617C17.736 12.8436 17.5084 12.8436 17.3379 12.9617L17.2686 13.0196L15.4968 14.7891L13.7274 13.0195L13.6581 12.9617C13.512 12.8604 13.3239 12.8459 13.1657 12.9183L13.0895 12.9617ZM7.99646 0C10.7579 0 12.9965 2.23858 12.9965 5C12.9965 7.76142 10.7579 10 7.99646 10C5.23503 10 2.99646 7.76142 2.99646 5C2.99646 2.23858 5.23503 0 7.99646 0ZM7.99646 1.5C6.06346 1.5 4.49646 3.067 4.49646 5C4.49646 6.933 6.06346 8.5 7.99646 8.5C9.92945 8.5 11.4965 6.933 11.4965 5C11.4965 3.067 9.92945 1.5 7.99646 1.5Z"
      fill={color}
    />
  </svg>
)

/**
 * User Icon - For viewing profile
 * Used in membersTab dropdown menu
 */
export const UserProfileIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 16,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 16 20" fill="none" className={className} {...props}>
    <path
      d="M13.7506 11.9953C14.9927 11.9953 15.9995 13.0022 15.9995 14.2442V14.8196C15.9995 15.7139 15.6799 16.5787 15.0984 17.2581C13.529 19.0916 11.1418 19.9964 7.99646 19.9964C4.8506 19.9964 2.46458 19.0913 0.89828 17.257C0.318519 16.5781 0 15.7146 0 14.8219V14.2442C0 13.0022 1.00686 11.9953 2.24888 11.9953H13.7506ZM13.7506 13.4953H2.24888C1.83528 13.4953 1.5 13.8306 1.5 14.2442V14.8219C1.5 15.3575 1.69111 15.8756 2.03897 16.283C3.29228 17.7507 5.25815 18.4964 7.99646 18.4964C10.7348 18.4964 12.7024 17.7506 13.9588 16.2827C14.3078 15.8751 14.4995 15.3562 14.4995 14.8196V14.2442C14.4995 13.8306 14.1642 13.4953 13.7506 13.4953ZM7.99646 0C10.7579 0 12.9965 2.23858 12.9965 5C12.9965 7.76142 10.7579 10 7.99646 10C5.23503 10 2.99646 7.76142 2.99646 5C2.99646 2.23858 5.23503 0 7.99646 0ZM7.99646 1.5C6.06346 1.5 4.49646 3.067 4.49646 5C4.49646 6.933 6.06346 8.5 7.99646 8.5C9.92945 8.5 11.4965 6.933 11.4965 5C11.4965 3.067 9.92945 1.5 7.99646 1.5Z"
      fill={color}
    />
  </svg>
)

/**
 * Shield with Check Icon - For assign role action
 * Used in membersTab dropdown menu
 */
export const ShieldCheckIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 21,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M8.55 0.15C5.89903 0.15 3.75 2.29903 3.75 4.95C3.75 7.60097 5.89903 9.75 8.55 9.75C11.201 9.75 13.35 7.60097 13.35 4.95C13.35 2.29903 11.201 0.15 8.55 0.15ZM4.95 4.95C4.95 2.96178 6.56178 1.35 8.55 1.35C10.5382 1.35 12.15 2.96178 12.15 4.95C12.15 6.93823 10.5382 8.55 8.55 8.55C6.56178 8.55 4.95 6.93823 4.95 4.95ZM2.56047 10.95C1.23378 10.95 0.15 12.0142 0.15 13.35C0.15 15.3794 1.14937 16.9096 2.71199 17.9059C4.25036 18.8868 6.32431 19.35 8.55 19.35C9.04303 19.35 9.52861 19.3273 10.0026 19.2812C9.71758 18.9266 9.46848 18.5419 9.2608 18.1326C9.02757 18.1442 8.7905 18.15 8.55 18.15C6.46787 18.15 4.64182 17.7132 3.35714 16.8941C2.09672 16.0904 1.35 14.9206 1.35 13.35C1.35 12.686 1.88743 12.15 2.56047 12.15L9.26965 12.15C9.48912 11.7207 9.75424 11.3185 10.0586 10.95L2.56047 10.95ZM10.5121 14.4539C11.8176 14.1204 12.5928 12.7777 12.2289 11.4803L12.0414 10.8122C12.3463 10.5779 12.6768 10.3771 13.0277 10.2152L13.4326 10.6448C14.3793 11.6493 15.9762 11.6502 16.924 10.6466L17.3136 10.2341C17.6709 10.4027 18.0067 10.6118 18.3154 10.8558L18.1639 11.3625C17.7687 12.6851 18.5679 14.0676 19.9112 14.3853L20.33 14.4843C20.3559 14.7024 20.3693 14.9245 20.3693 15.1498C20.3693 15.337 20.3601 15.522 20.3421 15.7043L19.7895 15.8455C18.4839 16.179 17.7087 17.5216 18.0727 18.819L18.2601 19.487C17.9552 19.7214 17.6247 19.9222 17.2738 20.0841L16.869 19.6546C15.9223 18.65 14.3253 18.6492 13.3776 19.6527L12.9876 20.0657C12.6303 19.8971 12.2946 19.6881 11.986 19.4442L12.1376 18.9369C12.5328 17.6143 11.7336 16.2317 10.3903 15.9141L9.97083 15.8149C9.94496 15.597 9.93164 15.375 9.93164 15.1498C9.93164 14.9626 9.94085 14.7776 9.95883 14.5952L10.5121 14.4539ZM16.3505 15.1498C16.3505 14.4871 15.8132 13.9498 15.1505 13.9498C14.4877 13.9498 13.9505 14.4871 13.9505 15.1498C13.9505 15.8126 14.4877 16.3498 15.1505 16.3498C15.8132 16.3498 16.3505 15.8126 16.3505 15.1498Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.6 4.95C3.6 2.21619 5.81619 0 8.55 0C11.2838 0 13.5 2.21619 13.5 4.95C13.5 7.68381 11.2838 9.9 8.55 9.9C5.81619 9.9 3.6 7.68381 3.6 4.95ZM8.55 0.3C5.98188 0.3 3.9 2.38188 3.9 4.95C3.9 7.51812 5.98188 9.6 8.55 9.6C11.1181 9.6 13.2 7.51812 13.2 4.95C13.2 2.38188 11.1181 0.3 8.55 0.3ZM8.55 1.5C6.64462 1.5 5.1 3.04462 5.1 4.95C5.1 6.85538 6.64462 8.4 8.55 8.4C10.4554 8.4 12 6.85538 12 4.95C12 3.04462 10.4554 1.5 8.55 1.5ZM4.8 4.95C4.8 2.87893 6.47893 1.2 8.55 1.2C10.6211 1.2 12.3 2.87893 12.3 4.95C12.3 7.02107 10.6211 8.7 8.55 8.7C6.47893 8.7 4.8 7.02107 4.8 4.95ZM13.0629 10.0338L13.5417 10.5419C14.4293 11.4837 15.9264 11.4845 16.8149 10.5436L17.2792 10.052L17.3776 10.0984C17.7454 10.272 18.0909 10.4872 18.4084 10.7381L18.4882 10.8012L18.3077 11.4055C17.9371 12.6454 18.6864 13.9415 19.9457 14.2393L20.4666 14.3624L20.479 14.4666C20.5056 14.6905 20.5193 14.9186 20.5193 15.1498C20.5193 15.3419 20.5098 15.5318 20.4914 15.719L20.4811 15.8236L19.8266 15.9908C18.6026 16.3034 17.8759 17.5622 18.2171 18.7785L18.4319 19.5441L18.3515 19.6059C18.0379 19.847 17.6979 20.0537 17.3367 20.2203L17.2387 20.2656L16.7598 19.7575C15.8723 18.8157 14.3752 18.8149 13.4866 19.7557L13.0219 20.2478L12.9236 20.2013C12.5558 20.0278 12.2104 19.8127 11.893 19.5619L11.8131 19.4988L11.9939 18.8939C12.3644 17.654 11.6152 16.3579 10.3558 16.0601L9.83424 15.9368L9.82188 15.8326C9.79531 15.6088 9.78164 15.3809 9.78164 15.1498C9.78164 14.9577 9.79109 14.7677 9.80955 14.5805L9.81986 14.4759L10.475 14.3086C11.6989 13.9959 12.4256 12.7372 12.0844 11.5209L11.8696 10.7551L11.95 10.6933C12.2636 10.4522 12.6037 10.2456 12.9649 10.079L13.0629 10.0338ZM12.2135 10.8704L12.3733 11.4398C12.76 12.8183 11.9363 14.2449 10.5492 14.5992L10.0988 14.7143C10.0874 14.8579 10.0816 15.0031 10.0816 15.1498C10.0816 15.3334 10.0907 15.5147 10.1085 15.6933L10.4248 15.7681C11.8521 16.1056 12.7012 17.5746 12.2813 18.9798L12.1592 19.3886C12.408 19.5773 12.6739 19.743 12.9541 19.8827L13.2685 19.5498C14.2755 18.4835 15.9723 18.4844 16.9781 19.5517L17.3081 19.9018C17.5823 19.768 17.8432 19.6094 18.0879 19.4289L17.9282 18.8595C17.5415 17.4811 18.3652 16.0545 19.7523 15.7001L20.2021 15.5852C20.2135 15.4417 20.2193 15.2965 20.2193 15.1498C20.2193 14.9661 20.2102 14.7846 20.1924 14.6059L19.8767 14.5312C18.4494 14.1938 17.6003 12.7248 18.0202 11.3196L18.1422 10.9114C17.8933 10.7226 17.6273 10.5569 17.3471 10.4171L17.033 10.7496C16.026 11.8159 14.3293 11.815 13.3234 10.7477L12.9934 10.3975C12.7192 10.5313 12.4583 10.6898 12.2135 10.8704ZM0 13.35C0 11.9302 1.15216 10.8 2.56047 10.8L10.377 10.8L10.1742 11.0455C9.87677 11.4057 9.61768 11.7987 9.40321 12.2183L9.36143 12.3L2.56047 12.3C1.96931 12.3 1.5 12.7698 1.5 13.35C1.5 14.8633 2.21522 15.9881 3.43779 16.7676C4.69076 17.5665 6.48583 18 8.55 18C8.78807 18 9.02267 17.9942 9.25336 17.9828L9.35053 17.978L9.39456 18.0647C9.59751 18.4647 9.84095 18.8406 10.1195 19.1873L10.2934 19.4037L10.0171 19.4305C9.53804 19.4771 9.04762 19.5 8.55 19.5C6.30638 19.5 4.20144 19.0335 2.63135 18.0324C1.03102 17.012 0 15.4369 0 13.35ZM2.56047 11.1C1.3154 11.1 0.3 12.0983 0.3 13.35C0.3 15.322 1.26773 16.8071 2.79264 17.7794C4.29929 18.7401 6.34224 19.2 8.55 19.2C8.94458 19.2 9.33413 19.1853 9.71648 19.1556C9.51386 18.8812 9.33151 18.5909 9.17166 18.2869C8.96714 18.2956 8.75982 18.3 8.55 18.3C6.44991 18.3 4.59288 17.8599 3.2765 17.0206C1.97822 16.1928 1.2 14.9778 1.2 13.35C1.2 12.6022 1.80556 12 2.56047 12L9.17857 12C9.34544 11.6843 9.53658 11.3835 9.74951 11.1L2.56047 11.1ZM13.8005 15.1498C13.8005 14.4042 14.4049 13.7998 15.1505 13.7998C15.896 13.7998 16.5005 14.4042 16.5005 15.1498C16.5005 15.8954 15.896 16.4998 15.1505 16.4998C14.4049 16.4998 13.8005 15.8954 13.8005 15.1498ZM15.1505 14.0998C14.5706 14.0998 14.1005 14.5699 14.1005 15.1498C14.1005 15.7297 14.5706 16.1998 15.1505 16.1998C15.7304 16.1998 16.2005 15.7297 16.2005 15.1498C16.2005 14.5699 15.7304 14.0998 15.1505 14.0998Z"
      fill={color}
    />
  </svg>
)

/**
 * Warning/Alert Icon - For report action
 * Used in membersTab dropdown menu
 */
export const WarningIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 18,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 18 19" fill="none" className={className} {...props}>
    <path
      d="M0 0.75C0 0.335786 0.335786 0 0.75 0H17.2541C17.8722 0 18.225 0.705647 17.8541 1.20006L13.6898 6.75122L17.8541 12.3024C18.225 12.7968 17.8722 13.5024 17.2541 13.5024L1.5 13.502V18.2518C1.5 18.6315 1.21785 18.9453 0.851771 18.995L0.75 19.0018C0.370304 19.0018 0.0565091 18.7197 0.00684667 18.3536L0 18.2518V0.75ZM15.7539 1.5H1.5V12.0024H15.7539L12.1522 7.20129C11.9522 6.93459 11.9522 6.56785 12.1522 6.30116L15.7539 1.5Z"
      fill={color}
    />
  </svg>
)

/**
 * User Minus Icon - For remove member action
 * Used in membersTab dropdown menu
 */
export const UserMinusIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 20,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 20 10" fill="none" className={className} {...props}>
    <path
      d="M7.25 0C7.66421 0 8 0.335786 8 0.75C8 1.12656 7.72249 1.4383 7.36083 1.49187L7.25 1.5H5C3.067 1.5 1.5 3.067 1.5 5C1.5 6.86396 2.95707 8.3876 4.79435 8.49406L5 8.5H7.25C7.66421 8.5 8 8.83579 8 9.25C8 9.62656 7.72249 9.9383 7.36083 9.99187L7.25 10H5C2.23858 10 0 7.76142 0 5C0 2.32226 2.10496 0.136152 4.75045 0.00611925L5 0H7.25ZM15 0C17.7614 0 20 2.23858 20 5C20 7.67774 17.895 9.86385 15.2495 9.99388L15 10H12.75C12.3358 10 12 9.66421 12 9.25C12 8.87344 12.2775 8.5617 12.6392 8.50813L12.75 8.5H15C16.933 8.5 18.5 6.933 18.5 5C18.5 3.13604 17.0429 1.6124 15.2057 1.50594L15 1.5H12.75C12.3358 1.5 12 1.16421 12 0.75C12 0.373442 12.2775 0.0617003 12.6392 0.00813198L12.75 0H15ZM5 4.25H15C15.4142 4.25 15.75 4.58579 15.75 5C15.75 5.3797 15.4678 5.69349 15.1018 5.74315L15 5.75H5C4.58579 5.75 4.25 5.41421 4.25 5C4.25 4.6203 4.53215 4.30651 4.89823 4.25685L5 4.25H15H5Z"
      fill={color}
    />
  </svg>
)

/**
 * Crown Icon - For admin/owner role display
 * Used in membersTab for role badges
 */
export const CrownIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 16,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className} {...props}>
    <path
      d="M8 0.5L9.5 3.5L13 4L10.5 6.5L11 10L8 8.5L5 10L5.5 6.5L3 4L6.5 3.5L8 0.5Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * X (Twitter) Icon - For social links
 * Used in ProfileInfoSection.detail.tsx
 */
export const XIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" fill={color} className={className} aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

/**
 * Hamburger Icon - 3-dot menu for mobile/dropdown
 * Used in ProfileInfoSection.detail.tsx
 */
export const HamburgerIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 16,
  className,
  ...props
}) => (
  <svg viewBox="0 0 24 24" fill={color} className={className} aria-hidden="true" {...props}>
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
)

/**
 * Arrow Left Icon - Used for back navigation
 * Used in communityDetail.tsx
 */
export const ArrowLeftIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 17 15"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M7.08832 14.4728C7.36326 14.7347 7.79843 14.7241 8.06031 14.4492C8.32219 14.1742 8.31161 13.7391 8.03667 13.4772L2.30621 8.01879L15.8125 8.01879C16.1922 8.01879 16.5 7.71099 16.5 7.33129C16.5 6.9516 16.1922 6.64379 15.8125 6.64379L2.30612 6.64379L8.03667 1.18531C8.3116 0.923431 8.32219 0.488259 8.06031 0.213326C7.79843 -0.0616066 7.36325 -0.0721879 7.08832 0.189692L0.287637 6.6675C0.132788 6.815 0.0411644 7.00314 0.0127686 7.19873C0.00439023 7.24163 -3.96424e-09 7.28595 0 7.33129C3.97201e-09 7.37673 0.00440686 7.42113 0.0128176 7.4641C0.0412702 7.65957 0.132876 7.84758 0.287637 7.995L7.08832 14.4728Z"
      fill={color}
    />
  </svg>
)

/**
 * Eye Icon
 */
export const EyeIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 -5 17 20" fill="none" className={className} {...props}>
    <path
      d="M8.91456 3.21257C10.9396 3.21257 12.5812 4.85419 12.5812 6.87924C12.5812 8.90428 10.9396 10.5459 8.91456 10.5459C6.88951 10.5459 5.24789 8.90428 5.24789 6.87924C5.24789 4.85419 6.88951 3.21257 8.91456 3.21257ZM8.91456 4.58757C7.6489 4.58757 6.62289 5.61359 6.62289 6.87924C6.62289 8.14489 7.6489 9.17091 8.91456 9.17091C10.1802 9.17091 11.2062 8.14489 11.2062 6.87924C11.2062 5.61359 10.1802 4.58757 8.91456 4.58757ZM8.91456 0C13.1436 0 16.7943 2.88751 17.8072 6.93402C17.8995 7.30235 17.6756 7.67569 17.3073 7.76789C16.9389 7.86009 16.5656 7.63625 16.4734 7.26791C15.6128 3.8298 12.5091 1.375 8.91456 1.375C5.31841 1.375 2.21381 3.83191 1.35468 7.27205C1.26268 7.64044 0.889465 7.86449 0.521084 7.77249C0.152702 7.68049 -0.0713506 7.30728 0.0206485 6.9389C1.03182 2.88999 4.68367 0 8.91456 0Z"
      fill={color}
    />
  </svg>
)

/**
 * Eye Icon
 */
export const PencilIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 19 19" fill="none" className={className} {...props}>
    <path
      d="M17.3725 0.960756C16.0915 -0.320306 14.0145 -0.320244 12.7335 0.960893L1.77928 11.9164C1.40681 12.2889 1.14501 12.7574 1.023 13.2699L0.0187096 17.4879C-0.036588 17.7201 0.0325619 17.9644 0.201378 18.1333C0.370194 18.3021 0.614503 18.3712 0.846753 18.3159L5.06457 17.3117C5.57717 17.1896 6.04584 16.9277 6.41842 16.5551L17.3727 5.59956C18.6536 4.31853 18.6535 2.24171 17.3725 0.960756ZM13.7058 1.93311C14.4498 1.189 15.6562 1.18896 16.4003 1.93303C17.1443 2.67703 17.1443 3.88329 16.4003 4.62734L15.5833 5.4445L12.8889 2.75011L13.7058 1.93311ZM11.9167 3.72244L14.6111 6.41683L5.44609 15.5829C5.25345 15.7756 5.01112 15.911 4.74609 15.9741L1.61508 16.7196L2.36061 13.5884C2.42369 13.3234 2.55904 13.0812 2.7516 12.8886L11.9167 3.72244Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * Eye Icon
 */
export const SettingIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" className={className} {...props}>
    <path
      d="M8.59148 0C9.26429 0.00775454 9.93448 0.0854903 10.5912 0.231952C10.8779 0.295886 11.0923 0.534885 11.1248 0.826799L11.2808 2.22641C11.3515 2.86904 11.894 3.35577 12.5409 3.35645C12.7148 3.35672 12.8867 3.32051 13.0474 3.24943L14.3313 2.68543C14.5983 2.56813 14.9103 2.63208 15.1097 2.84498C16.0375 3.83592 16.7285 5.02439 17.1307 6.32095C17.2174 6.60053 17.1176 6.90436 16.882 7.07804L15.744 7.91688C15.4193 8.15539 15.2276 8.53417 15.2276 8.937C15.2276 9.33983 15.4193 9.71861 15.7447 9.95765L16.8837 10.7968C17.1194 10.9704 17.2193 11.2743 17.1325 11.5539C16.7305 12.8503 16.0399 14.0387 15.1126 15.0298C14.9134 15.2426 14.6017 15.3067 14.3347 15.1898L13.0455 14.625C12.6768 14.4636 12.2532 14.4872 11.9046 14.6886C11.5561 14.8901 11.3242 15.2453 11.2799 15.6454L11.1249 17.0449C11.0929 17.3334 10.8831 17.5708 10.6007 17.638C9.27293 17.9539 7.8896 17.9539 6.56185 17.638C6.2794 17.5708 6.06961 17.3334 6.03766 17.0449L5.8829 15.6475C5.83745 15.2481 5.60513 14.894 5.25688 14.6932C4.90862 14.4925 4.48576 14.4689 4.11817 14.6294L2.82877 15.1943C2.56169 15.3114 2.24985 15.2472 2.05071 15.0342C1.12289 14.0419 0.43225 12.8522 0.0307563 11.5544C-0.0557208 11.2749 0.0441954 10.9712 0.279764 10.7977L1.41949 9.95804C1.74411 9.71953 1.93584 9.34075 1.93584 8.93792C1.93584 8.53509 1.74411 8.15631 1.41906 7.91748L0.280054 7.07928C0.0441351 6.90566 -0.0558729 6.60163 0.0309091 6.32187C0.433098 5.0253 1.12411 3.83684 2.05194 2.8459C2.25128 2.633 2.56327 2.56904 2.8303 2.68635L4.11397 3.25025C4.48334 3.41234 4.90797 3.38786 5.25811 3.1833C5.60674 2.98108 5.83883 2.62554 5.88364 2.22534L6.03955 0.826799C6.0721 0.534737 6.2867 0.295662 6.57356 0.231864C7.23106 0.0856348 7.90194 0.00793258 8.59148 0ZM8.59165 1.37491C8.17545 1.37981 7.76042 1.41573 7.34999 1.48226L7.25014 2.378C7.1568 3.2117 6.67369 3.95177 5.94987 4.37161C5.22163 4.79707 4.33374 4.84827 3.56119 4.50924L2.73776 4.14751C2.21349 4.78383 1.7962 5.50124 1.5023 6.27154L2.23362 6.80972C2.91086 7.30731 3.31084 8.09753 3.31084 8.93792C3.31084 9.77831 2.91086 10.5685 2.23433 11.0656L1.50186 11.6052C1.79551 12.3769 2.21286 13.0956 2.73749 13.7332L3.56719 13.3696C4.33545 13.0342 5.21729 13.0833 5.94356 13.502C6.66982 13.9206 7.15429 14.6591 7.24932 15.4941L7.3492 16.396C8.1647 16.5346 8.99783 16.5346 9.81333 16.396L9.91321 15.4941C10.0056 14.6594 10.4895 13.9184 11.2166 13.4982C11.9438 13.0779 12.8274 13.0286 13.5971 13.3654L14.4261 13.7286C14.9503 13.0921 15.3674 12.3745 15.6613 11.6041L14.9298 11.0652C14.2526 10.5676 13.8526 9.77739 13.8526 8.937C13.8526 8.09661 14.2526 7.3064 14.929 6.80943L15.6595 6.271C15.3655 5.50056 14.9482 4.78302 14.4239 4.1466L13.6021 4.50759C13.2672 4.65576 12.9049 4.73202 12.5391 4.73145C11.1919 4.73004 10.0612 3.71575 9.91419 2.37767L9.81434 1.48197C9.40593 1.41551 8.99524 1.37969 8.59165 1.37491ZM8.58004 5.49995C10.4785 5.49995 12.0175 7.03898 12.0175 8.93745C12.0175 10.8359 10.4785 12.375 8.58004 12.375C6.68156 12.375 5.14254 10.8359 5.14254 8.93745C5.14254 7.03898 6.68156 5.49995 8.58004 5.49995ZM8.58004 6.87495C7.44096 6.87495 6.51754 7.79837 6.51754 8.93745C6.51754 10.0765 7.44096 11 8.58004 11C9.71913 11 10.6425 10.0765 10.6425 8.93745C10.6425 7.79837 9.71913 6.87495 8.58004 6.87495Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * Eye Icon
 */
export const UploadIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 17 19" fill="none" className={className} {...props}>
    <path
      d="M15.3542 8.25C15.7022 8.25 15.9899 8.50864 16.0354 8.84421L16.0417 8.9375V15.355C16.0417 16.9436 14.7982 18.2418 13.2316 18.3294L13.0625 18.3341H2.97917C1.39055 18.3341 0.0923579 17.0907 0.0047161 15.524L0 15.355V8.9375C0 8.5578 0.307804 8.25 0.6875 8.25C1.03555 8.25 1.3232 8.50864 1.36872 8.84421L1.375 8.9375V15.355C1.375 16.1966 2.02318 16.8869 2.8476 16.9538L2.97917 16.9591H13.0625C13.9042 16.9591 14.5944 16.3109 14.6613 15.4865L14.6667 15.355V8.9375C14.6667 8.5578 14.9745 8.25 15.3542 8.25ZM2.94745 4.78161L7.52733 0.201382C7.77127 -0.0425688 8.15295 -0.0649011 8.4221 0.134493L8.49922 0.200964L13.087 4.78119C13.3557 5.04946 13.3561 5.48476 13.0878 5.75346C12.8439 5.99774 12.462 6.02025 12.1927 5.82077L12.1155 5.75426L8.70283 2.34758L8.70364 13.067C8.70364 13.4151 8.445 13.7027 8.10944 13.7483L8.01614 13.7545C7.66809 13.7545 7.38045 13.4959 7.33492 13.1603L7.32864 13.067L7.32783 2.34483L3.91975 5.75385C3.67569 5.99793 3.29375 6.02014 3.02459 5.82045L2.94748 5.75388C2.7034 5.50981 2.68119 5.12787 2.88088 4.85872L2.94745 4.78161L7.52733 0.201382L2.94745 4.78161Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * User Icon
 */
export const UserIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 16 20" fill="none" className={className} {...props}>
    <path
      d="M13.7506 11.9953C14.9927 11.9953 15.9995 13.0022 15.9995 14.2442V14.8196C15.9995 15.7139 15.6799 16.5787 15.0984 17.2581C13.529 19.0916 11.1418 19.9964 7.99646 19.9964C4.8506 19.9964 2.46458 19.0913 0.89828 17.257C0.318519 16.5781 0 15.7146 0 14.8219V14.2442C0 13.0022 1.00686 11.9953 2.24888 11.9953H13.7506ZM13.7506 13.4953H2.24888C1.83528 13.4953 1.5 13.8306 1.5 14.2442V14.8219C1.5 15.3575 1.69111 15.8756 2.03897 16.283C3.29228 17.7507 5.25815 18.4964 7.99646 18.4964C10.7348 18.4964 12.7024 17.7506 13.9588 16.2827C14.3078 15.8751 14.4995 15.3562 14.4995 14.8196V14.2442C14.4995 13.8306 14.1642 13.4953 13.7506 13.4953ZM7.99646 0C10.7579 0 12.9965 2.23858 12.9965 5C12.9965 7.76142 10.7579 10 7.99646 10C5.23503 10 2.99646 7.76142 2.99646 5C2.99646 2.23858 5.23503 0 7.99646 0ZM7.99646 1.5C6.06346 1.5 4.49646 3.067 4.49646 5C4.49646 6.933 6.06346 8.5 7.99646 8.5C9.92945 8.5 11.4965 6.933 11.4965 5C11.4965 3.067 9.92945 1.5 7.99646 1.5Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * Pin Icon
 */
export const PinIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 19 19" fill="none" className={className} {...props}>
    <path
      d="M18.0682 5.63167L13.2425 0.80591C11.9152 -0.521356 9.67766 -0.158253 8.83823 1.52062L6.40281 6.3915C6.31512 6.56689 6.16223 6.70096 5.97688 6.76499L1.81061 8.20425C0.937914 8.50572 0.682002 9.61674 1.33487 10.2696L4.43936 13.3741L8.27878e-05 17.8135L0 18.8741H1.06074L5.50002 14.4348L8.60453 17.5393C9.2574 18.1921 10.3684 17.9362 10.6699 17.0635L12.1092 12.8973C12.1732 12.7119 12.3073 12.559 12.4826 12.4713L17.3535 10.0359C19.0324 9.19645 19.3955 6.95893 18.0682 5.63167Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * Send Icon
 */
export const SendIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M3.59117 10.159L0.0554595 1.06711C-0.190623 0.43433 0.4264 -0.170812 1.03678 0.0442873L1.13297 0.085182L19.883 9.46018C20.4228 9.7301 20.4565 10.4692 19.9842 10.7977L19.883 10.8577L1.13297 20.2327C0.525702 20.5364 -0.133989 19.978 0.02365 19.3504L0.0554595 19.2508L3.59117 10.159L0.0554595 1.06711L3.59117 10.159ZM2.24584 2.38854L4.96354 9.37708L11.8673 9.3777C12.2628 9.3777 12.5896 9.67161 12.6414 10.0529L12.6485 10.159C12.6485 10.5545 12.3546 10.8813 11.9733 10.9331L11.8673 10.9402L4.96354 10.9396L2.24584 17.9294L17.7867 10.159L2.24584 2.38854Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * Lock Icon
 */
export const LockIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 15 18"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M7.33333 12.375C8.09272 12.375 8.70833 11.7594 8.70833 11C8.70833 10.2406 8.09272 9.625 7.33333 9.625C6.57394 9.625 5.95833 10.2406 5.95833 11C5.95833 11.7594 6.57394 12.375 7.33333 12.375ZM3.66667 3.66667C3.66667 1.64162 5.30829 0 7.33333 0C9.35838 0 11 1.64162 11 3.66667V4.58333H11.6875C13.3328 4.58333 14.6667 5.91715 14.6667 7.5625V14.4375C14.6667 16.0828 13.3328 17.4167 11.6875 17.4167H2.97917C1.33382 17.4167 0 16.0828 0 14.4375V7.5625C0 5.91715 1.33382 4.58333 2.97917 4.58333H3.66667V3.66667ZM7.33333 1.375C6.06768 1.375 5.04167 2.40101 5.04167 3.66667V4.58333H9.625V3.66667C9.625 2.40101 8.59899 1.375 7.33333 1.375ZM2.97917 5.95833C2.09321 5.95833 1.375 6.67654 1.375 7.5625V14.4375C1.375 15.3235 2.09321 16.0417 2.97917 16.0417H11.6875C12.5735 16.0417 13.2917 15.3235 13.2917 14.4375V7.5625C13.2917 6.67654 12.5735 5.95833 11.6875 5.95833H2.97917Z"
      fill={color}
    />
  </svg>
)

/**
 * User Invited Icon
 */
export const UserInvitedIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className} {...props}>
    <path
      d="M14.1666 11.2513C14.1666 10.5609 13.607 10.0013 12.9166 10.0013H7.08329C6.39294 10.0013 5.83329 10.5609 5.83329 11.2513V11.668C5.83329 13.3108 7.38288 15.0013 9.99996 15.0013C12.617 15.0013 14.1666 13.3108 14.1666 11.668V11.2513ZM12.2916 6.8763C12.2916 5.61065 11.2656 4.58464 9.99996 4.58464C8.73431 4.58464 7.70829 5.61065 7.70829 6.8763C7.70829 8.14195 8.73431 9.16797 9.99996 9.16797C11.2656 9.16797 12.2916 8.14195 12.2916 6.8763ZM18.3333 10.0013C18.3333 14.6037 14.6023 18.3346 9.99996 18.3346C5.39759 18.3346 1.66663 14.6037 1.66663 10.0013C1.66663 5.39893 5.39759 1.66797 9.99996 1.66797C14.6023 1.66797 18.3333 5.39893 18.3333 10.0013ZM17.0833 10.0013C17.0833 6.08928 13.912 2.91797 9.99996 2.91797C6.08794 2.91797 2.91663 6.08928 2.91663 10.0013C2.91663 13.9133 6.08794 17.0846 9.99996 17.0846C13.912 17.0846 17.0833 13.9133 17.0833 10.0013Z"
      fill="#344653"
    />
  </svg>
)

/**
 * Invite Accepted Icon
 */
export const InviteAcceptedIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 17 17" fill="none" className={className} {...props}>
    <path
      d="M8.33333 0C12.9357 0 16.6667 3.73096 16.6667 8.33333C16.6667 12.9357 12.9357 16.6667 8.33333 16.6667C3.73096 16.6667 0 12.9357 0 8.33333C0 3.73096 3.73096 0 8.33333 0ZM11.0164 5.80806L7.29167 9.53278L5.65027 7.89139C5.4062 7.64731 5.01047 7.64731 4.76639 7.89139C4.52231 8.13547 4.52231 8.5312 4.76639 8.77527L6.84973 10.8586C7.0938 11.1027 7.48953 11.1027 7.73361 10.8586L11.9003 6.69194C12.1444 6.44786 12.1444 6.05214 11.9003 5.80806C11.6562 5.56398 11.2605 5.56398 11.0164 5.80806Z"
      fill="#5A5B3F"
    />
  </svg>
)

/*
 * Invite Declined Icon
 */
export const InviteDeclinedIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 17,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 17 17" fill="none" className={className} {...props}>
    <path
      d="M8.33333 0C12.9357 0 16.6667 3.73096 16.6667 8.33333C16.6667 12.9357 12.9357 16.6667 8.33333 16.6667C3.73096 16.6667 0 12.9357 0 8.33333C0 3.73096 3.73096 0 8.33333 0ZM11.2753 5.39139L11.2052 5.33088C10.9877 5.1695 10.6892 5.16751 10.4697 5.3249L10.3914 5.39139L8.33333 7.44917L6.27527 5.39139L6.20518 5.33088C5.98769 5.1695 5.68917 5.16751 5.46968 5.3249L5.39139 5.39139L5.33088 5.46149C5.1695 5.67898 5.16751 5.97749 5.3249 6.19698L5.39139 6.27527L7.44917 8.33333L5.39139 10.3914L5.33088 10.4615C5.1695 10.679 5.16751 10.9775 5.3249 11.197L5.39139 11.2753L5.46149 11.3358C5.67898 11.4972 5.97749 11.4992 6.19698 11.3418L6.27527 11.2753L8.33333 9.2175L10.3914 11.2753L10.4615 11.3358C10.679 11.4972 10.9775 11.4992 11.197 11.3418L11.2753 11.2753L11.3358 11.2052C11.4972 10.9877 11.4992 10.6892 11.3418 10.4697L11.2753 10.3914L9.2175 8.33333L11.2753 6.27527L11.3358 6.20518C11.4972 5.98769 11.4992 5.68917 11.3418 5.46968L11.2753 5.39139L11.2052 5.33088L11.2753 5.39139Z"
      fill={color}
    />
  </svg>
)

/**
 * Referral Link Icon - For invite friends section
 */
export const ReferralLinkIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 14,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 14 17" fill="none" className={className} {...props}>
    <path
      d="M1.25234 2.1892L1.25 3.95833V12.7118C1.25 14.2076 2.46256 15.4202 3.95833 15.4202L11.1386 15.4204C10.8811 16.1485 10.1866 16.6702 9.37034 16.6702H3.95833C1.77221 16.6702 0 14.898 0 12.7118V3.95833C0 3.14107 0.522871 2.44594 1.25234 2.1892ZM11.4583 0C12.4939 0 13.3333 0.839466 13.3333 1.875V12.7083C13.3333 13.7439 12.4939 14.5833 11.4583 14.5833H3.95833C2.9228 14.5833 2.08333 13.7439 2.08333 12.7083V1.875C2.08333 0.839466 2.9228 0 3.95833 0H11.4583ZM11.4583 1.25H3.95833C3.61316 1.25 3.33333 1.52982 3.33333 1.875V12.7083C3.33333 13.0535 3.61316 13.3333 3.95833 13.3333H11.4583C11.8035 13.3333 12.0833 13.0535 12.0833 12.7083V1.875C12.0833 1.52982 11.8035 1.25 11.4583 1.25Z"
      fill={color}
    />
  </svg>
)

/**
 * Referral Link Icon - For invite friends section
 */
export const AIGeneratedIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 14,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 34 34" fill="none" className={className} {...props}>
    <path
      d="M16.7517 0C17.8497 0.000408686 18.6813 0.92328 19.287 2.05144C19.8174 3.03956 20.2688 4.35892 20.639 5.90504C21.9266 4.97109 23.1296 4.26913 24.1744 3.8668C25.2907 3.43703 26.4364 3.27212 27.2582 3.82867L27.4195 3.95038L27.4253 3.95478L27.5778 4.09848L27.5837 4.10581C27.9462 4.4924 28.1183 5.00482 28.1673 5.55018L28.1805 5.78773C28.1966 6.35088 28.1016 6.98576 27.9224 7.66321C27.5616 9.02705 26.8315 10.6656 25.8241 12.4465C27.8308 12.8428 29.5455 13.3592 30.8141 13.9744C31.5344 14.3239 32.143 14.7193 32.5781 15.1651C32.9711 15.5679 33.2509 16.0449 33.304 16.586C33.3235 16.6474 33.3341 16.7124 33.3333 16.7781C33.3259 17.3832 33.0289 17.91 32.6001 18.3456C32.1732 18.7793 31.5808 19.1631 30.8786 19.4997C29.6516 20.0877 27.9988 20.5738 26.066 20.944C27.0556 22.6455 27.7746 24.211 28.138 25.522C28.3458 26.2722 28.4471 26.9708 28.4005 27.5779C28.3593 28.1122 28.1988 28.6163 27.855 29.0046L27.8491 29.012L27.6966 29.1645C27.2764 29.5342 26.7347 29.6727 26.1745 29.666C25.6184 29.6589 24.9985 29.5104 24.3489 29.2656C23.2456 28.8498 21.9674 28.1174 20.5994 27.1453C20.2014 28.784 19.7187 30.177 19.158 31.2144C18.8278 31.8253 18.4575 32.3417 18.0406 32.7116C17.6202 33.0845 17.1219 33.3331 16.5625 33.3333H16.5552C15.4638 33.3191 14.6344 32.403 14.0272 31.2746C13.4964 30.2879 13.0428 28.9717 12.6723 27.4254C11.3851 28.359 10.1844 29.0628 9.13982 29.4651C7.94922 29.9235 6.72505 30.081 5.89623 29.383L5.72907 29.2246C5.36814 28.8391 5.19598 28.328 5.14692 27.7832C5.09119 27.1631 5.18684 26.4436 5.39181 25.6687C5.7527 24.3048 6.47817 22.6639 7.48577 20.8825C5.48068 20.4862 3.76638 19.9723 2.49868 19.3574C1.77857 19.0081 1.17114 18.6124 0.736113 18.1668C0.299381 17.7191 0.000196155 17.1797 0 16.5626C0 15.9542 0.294223 15.4232 0.722915 14.9862C1.14937 14.5516 1.74414 14.17 2.44589 13.8337C3.67166 13.2462 5.32098 12.7566 7.24528 12.3864C6.25639 10.6857 5.53793 9.12025 5.17478 7.80984C4.96698 7.05966 4.86709 6.36103 4.91377 5.754C4.95489 5.21985 5.11427 4.71553 5.45779 4.32723L5.46512 4.3199L5.61029 4.17327L5.61762 4.1674C6.0382 3.79717 6.57958 3.66059 7.13971 3.66737C7.6956 3.67428 8.31401 3.82143 8.96386 4.06622C10.0661 4.48163 11.3438 5.21296 12.7104 6.18365C13.1085 4.54579 13.5958 3.15428 14.1562 2.11743C14.4865 1.50667 14.8567 0.990124 15.2736 0.620271C15.6941 0.247348 16.1929 0.000392773 16.7517 0ZM16.74 23.9911C15.8039 24.892 14.8817 25.7066 14.0023 26.418C14.3598 27.9463 14.7925 29.171 15.2633 30.0164C15.7961 30.9728 16.2602 31.2436 16.5625 31.2438H16.5801C16.7371 31.2438 16.9377 31.1732 17.1725 30.971C17.4077 30.7685 17.6705 30.4399 17.9468 29.9416C18.4413 29.0496 18.8904 27.7592 19.2665 26.157C18.4408 25.5096 17.5959 24.7847 16.74 23.9911ZM9.06504 21.1669C8.0941 22.8731 7.43465 24.3858 7.13237 25.5733C6.97075 26.2086 6.92103 26.7184 6.95934 27.0998C6.99731 27.4769 7.11513 27.6767 7.24382 27.7846L7.27314 27.8154C7.53028 27.9981 8.05874 28.0692 9.02544 27.7142C9.93258 27.381 11.0529 26.7294 12.3204 25.8021C12.0897 24.4866 11.9015 23.0434 11.7763 21.4939C10.8382 21.4043 9.92842 21.3006 9.06504 21.1669ZM24.4765 21.2021C23.5354 21.3422 22.5389 21.4583 21.4866 21.5423C21.3498 22.9598 21.1634 24.2984 20.9367 25.5176C22.2777 26.4829 23.4688 27.1608 24.4281 27.5089C25.449 27.8794 25.9982 27.8039 26.2552 27.6175C26.2699 27.6008 26.2865 27.5839 26.305 27.5676C26.4264 27.4606 26.5372 27.2638 26.5704 26.8989C26.6038 26.5298 26.5513 26.0378 26.3901 25.4267C26.0889 24.285 25.4369 22.8367 24.4765 21.2021ZM13.2764 21.6523C13.3759 22.7634 13.5128 23.8085 13.6811 24.7786C14.3221 24.2442 14.9839 23.6553 15.6622 23.0145C15.2381 22.5986 14.8148 22.1641 14.3953 21.7212C14.0211 21.7007 13.6459 21.6775 13.2764 21.6523ZM18.9776 21.7241C18.5848 22.1606 18.1865 22.5791 17.7943 22.9794C18.4037 23.5384 19.0051 24.053 19.5906 24.5278C19.7471 23.6346 19.8806 22.6802 19.9865 21.6772C19.6569 21.6952 19.3219 21.7121 18.9776 21.7241ZM16.7238 21.9925C16.796 21.9204 16.8675 21.8479 16.9394 21.7755H16.6373C16.5915 21.7755 16.5455 21.772 16.4995 21.7711C16.5747 21.845 16.6487 21.9196 16.7238 21.9925ZM14.9994 13.0726C14.6324 13.4767 14.2749 13.8859 13.9026 14.3234L13.9011 14.322C13.741 14.5197 13.5837 14.7152 13.426 14.9056C13.3067 15.0496 13.188 15.1895 13.0711 15.3323C13.0474 15.7426 13.0462 16.1701 13.0462 16.6183V17.9849L13.9319 19.0172L13.9348 19.0216L15.0229 20.2065C15.5557 20.2348 16.0882 20.2534 16.6197 20.2534H16.6329L17.1168 20.2563C17.5128 20.2541 17.8764 20.2424 18.2928 20.2373C18.6599 19.8332 19.0207 19.428 19.3926 18.9908C19.553 18.7927 19.7133 18.5987 19.8706 18.4087C19.9885 18.2664 20.1027 18.1241 20.2196 17.9805C20.2437 17.5697 20.2504 17.1414 20.2504 16.6945V15.3279C19.9525 14.9763 19.6703 14.6348 19.3677 14.3L19.3603 14.2926C19.0032 13.8864 18.639 13.5048 18.2679 13.1034C17.7374 13.0754 17.2071 13.0594 16.6769 13.0594H16.6593C16.0875 13.0431 15.5805 13.0655 14.9994 13.0726ZM21.6831 18.5436C21.6674 19.069 21.6387 19.5791 21.6053 20.0804C22.3051 20.0182 22.976 19.9395 23.6187 19.8487C23.1936 19.2007 22.7272 18.5307 22.2256 17.85C22.0477 18.0801 21.8679 18.3121 21.6831 18.5436ZM11.137 18.0084C10.6977 18.6282 10.2888 19.2337 9.91553 19.8267C10.4848 19.9078 11.0765 19.9825 11.6854 20.0481C11.6545 19.5796 11.6302 19.1025 11.6238 18.6169C11.461 18.4131 11.2977 18.2102 11.137 18.0084ZM25.0132 13.8087C24.457 14.7162 23.8256 15.6443 23.145 16.5889C23.9141 17.6048 24.6091 18.6037 25.2287 19.5569C27.0784 19.2015 28.5928 18.7461 29.6498 18.2254C30.218 17.9454 30.6255 17.6632 30.8815 17.3954C31.134 17.1313 31.2084 16.917 31.2085 16.7517V16.7444C31.2107 16.5719 31.134 16.3493 30.8742 16.0743C30.6111 15.7959 30.1926 15.5004 29.6073 15.2091C28.5132 14.6648 26.9403 14.1828 25.0132 13.8087ZM8.09871 13.7691C6.24288 14.1248 4.72412 14.5845 3.66443 15.1065C3.09635 15.3864 2.68889 15.6699 2.43269 15.9379C2.22019 16.1603 2.13618 16.3475 2.11449 16.4995C2.11864 16.5258 2.1251 16.5554 2.12476 16.5875C2.12259 16.7606 2.19836 16.9846 2.45762 17.2591C2.72055 17.5372 3.13918 17.8314 3.72455 18.1228C4.81799 18.6669 6.39096 19.1462 8.31719 19.5202C8.87333 18.6128 9.50481 17.6846 10.1853 16.74C9.41536 15.7229 8.71896 14.7234 8.09871 13.7691ZM11.7045 13.3072C11.0052 13.3692 10.3347 13.4469 9.69264 13.5375C10.1173 14.1848 10.5846 14.8531 11.0857 15.5332C11.263 15.3039 11.4426 15.0732 11.6268 14.8425C11.6424 14.3179 11.6713 13.8082 11.7045 13.3072ZM21.6244 13.338C21.6553 13.8066 21.6796 14.2836 21.686 14.7692C21.8487 14.9727 22.0119 15.176 22.1728 15.3777C22.6119 14.7581 23.0198 14.1525 23.3928 13.5594C22.824 13.4784 22.2328 13.4036 21.6244 13.338ZM26.0352 5.51205C25.776 5.33268 25.2483 5.26678 24.2888 5.6191C23.3812 5.95245 22.2571 6.5994 20.9895 7.52683C21.2203 8.84239 21.4082 10.2854 21.5335 11.835C22.4714 11.9245 23.3813 12.0285 24.2448 12.162C25.2157 10.456 25.8782 8.94571 26.1804 7.75852C26.342 7.12323 26.3932 6.6134 26.3549 6.23204C26.3264 5.94956 26.2532 5.76623 26.1643 5.64696L26.0704 5.54725C26.0537 5.53327 26.0411 5.51908 26.0352 5.51205ZM8.9052 5.82292C7.88611 5.45314 7.33426 5.52455 7.07665 5.71001C7.06357 5.72576 7.04805 5.74682 7.02826 5.76427C6.90678 5.87121 6.79612 6.06768 6.76285 6.43293C6.72937 6.80204 6.782 7.29381 6.94321 7.90516C7.24429 9.0464 7.89439 10.4948 8.85388 12.1283C9.79459 11.9883 10.7907 11.872 11.8423 11.7881C11.9792 10.3706 12.1654 9.03177 12.3922 7.81277C11.0525 6.84868 9.86359 6.17073 8.9052 5.82292ZM19.6302 8.60754C18.9894 9.14166 18.327 9.7297 17.6491 10.3701C18.0734 10.7864 18.4964 11.2218 18.916 11.6649C19.2904 11.6856 19.6656 11.7099 20.0349 11.7353C19.9353 10.6235 19.7986 9.57807 19.6302 8.60754ZM13.7207 8.85829C13.5643 9.75097 13.4307 10.705 13.3248 11.7074C13.6542 11.6897 13.9887 11.6739 14.3322 11.662C14.7248 11.2256 15.1234 10.8072 15.5156 10.4068C14.9063 9.84789 14.3058 9.3329 13.7207 8.85829ZM16.3719 11.6136H16.6769C16.7218 11.6136 16.7668 11.6157 16.8118 11.6165C16.7361 11.5421 16.6616 11.467 16.586 11.3936C16.514 11.466 16.4438 11.5404 16.3719 11.6136ZM16.7326 2.10716C16.5759 2.10729 16.3762 2.17816 16.1417 2.3799C15.9066 2.58234 15.6436 2.91129 15.3675 3.40929C14.8731 4.30096 14.4208 5.59009 14.0448 7.19104C14.871 7.83868 15.7163 8.56433 16.5728 9.35832C17.5082 8.45814 18.4287 7.64253 19.3076 6.93149C18.9502 5.40445 18.5214 4.18086 18.0509 3.33597C17.5183 2.37993 17.0541 2.1076 16.7517 2.10716H16.7326Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * JoinedCommunityIcon
 */
export const JoinedCommunityIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 14,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 20 19" fill="none" className={className} {...props}>
    <path
      d="M3.20833 3.66667C3.20833 2.40101 4.23435 1.375 5.5 1.375C6.76565 1.375 7.79167 2.40101 7.79167 3.66667C7.79167 4.93232 6.76565 5.95833 5.5 5.95833C4.23435 5.95833 3.20833 4.93232 3.20833 3.66667ZM5.5 0C3.47496 0 1.83333 1.64162 1.83333 3.66667C1.83333 5.69171 3.47496 7.33333 5.5 7.33333C7.52504 7.33333 9.16667 5.69171 9.16667 3.66667C9.16667 1.64162 7.52504 0 5.5 0ZM12.375 4.58333C12.375 3.82394 12.9906 3.20833 13.75 3.20833C14.5094 3.20833 15.125 3.82394 15.125 4.58333C15.125 5.34273 14.5094 5.95833 13.75 5.95833C12.9906 5.95833 12.375 5.34273 12.375 4.58333ZM13.75 1.83333C12.2312 1.83333 11 3.06455 11 4.58333C11 6.10212 12.2312 7.33333 13.75 7.33333C15.2688 7.33333 16.5 6.10212 16.5 4.58333C16.5 3.06455 15.2688 1.83333 13.75 1.83333ZM2.0625 9.16667C0.923413 9.16667 0 10.0901 0 11.2292V11.4593L2.89579e-06 11.4604L1.27306e-05 11.4627L6.08116e-05 11.4685L0.000322089 11.4837C0.000589376 11.4956 0.00106969 11.5107 0.00192231 11.529C0.00362635 11.5655 0.00682473 11.6146 0.0128114 11.6744C0.0247606 11.7939 0.0479658 11.9579 0.0931795 12.1517C0.183285 12.5378 0.363499 13.0537 0.725841 13.5713C1.47644 14.6436 2.90747 15.5833 5.5 15.5833C6.75489 15.5833 7.73765 15.3632 8.50283 15.0144C8.36623 14.5613 8.28211 14.0855 8.25754 13.594C7.66636 13.9433 6.79495 14.2083 5.5 14.2083C3.28003 14.2083 2.30481 13.4293 1.85228 12.7828C1.61306 12.4411 1.4925 12.0976 1.43221 11.8392C1.40223 11.7107 1.38784 11.6061 1.38099 11.5376C1.37757 11.5035 1.37607 11.4787 1.37543 11.4649L1.375 11.4534V11.2292C1.375 10.8495 1.6828 10.5417 2.0625 10.5417H8.92119C9.14893 10.1047 9.42925 9.69952 9.75376 9.33449C9.50343 9.2265 9.22745 9.16667 8.9375 9.16667H2.0625ZM19.25 13.2917C19.25 16.0761 16.9928 18.3333 14.2083 18.3333C11.4239 18.3333 9.16667 16.0761 9.16667 13.2917C9.16667 10.5072 11.4239 8.25 14.2083 8.25C16.9928 8.25 19.25 10.5072 19.25 13.2917ZM17.2824 11.1342C17.1034 10.9553 16.8132 10.9553 16.6342 11.1342L13.2917 14.4768L11.7824 12.9676C11.6034 12.7886 11.3132 12.7886 11.1342 12.9676C10.9553 13.1466 10.9553 13.4368 11.1342 13.6158L12.9676 15.4491C13.1466 15.6281 13.4368 15.6281 13.6158 15.4491L17.2824 11.7824C17.4614 11.6034 17.4614 11.3132 17.2824 11.1342Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * UploadLinkIcon
 */
export const UploadLinkIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 14,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} {...props}>
    <path
      d="M10.1773 1.83098C12.6163 -0.610252 16.5736 -0.610252 19.0144 1.83053C21.3926 4.20872 21.4536 8.02664 19.1973 10.4787L19.0013 10.6843L9.83465 19.8491L9.79656 19.8806C8.27444 21.2522 5.92652 21.2056 4.4605 19.7396C3.08653 18.3656 2.95932 16.217 4.07889 14.6996C4.10321 14.6523 4.13263 14.6067 4.16723 14.5639L4.22301 14.5018L4.31354 14.4104L4.4605 14.2563L4.46354 14.2593L12.2091 6.49441C12.4861 6.21668 12.9201 6.19089 13.2262 6.41741L13.314 6.49294C13.5917 6.76993 13.6175 7.20392 13.391 7.51007L13.3154 7.59779L5.40437 15.5281C4.65683 16.4402 4.70879 17.7884 5.56024 18.6398C6.42385 19.5034 7.79849 19.5446 8.71076 18.7632L17.9115 9.56481C19.7401 7.73392 19.7401 4.76596 17.9095 2.93538C16.1362 1.162 13.2954 1.10658 11.4553 2.76913L11.2804 2.93538L11.2673 2.95029L1.33368 12.884C1.02858 13.1891 0.53392 13.1891 0.228823 12.884C-0.0485383 12.6066 -0.0737527 12.1726 0.153179 11.8667L0.228823 11.7791L10.1756 1.83053L10.1773 1.83098Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * BackIcon
 */
export const BackIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 14,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 18 16" fill="none" className={className} {...props}>
    <path
      d="M7.73272 15.7885C8.03264 16.0742 8.50738 16.0627 8.79306 15.7627C9.07875 15.4628 9.06721 14.9881 8.76728 14.7024L2.51587 8.74777L17.25 8.74777C17.6642 8.74777 18 8.41199 18 7.99777C18 7.58356 17.6642 7.24777 17.25 7.24777L2.51577 7.24777L8.76728 1.29307C9.06721 1.00738 9.07875 0.532646 8.79306 0.23272C8.50738 -0.0672072 8.03264 -0.0787504 7.73272 0.206937L0.313785 7.27364C0.144859 7.43455 0.0449066 7.63979 0.0139294 7.85316C0.00478934 7.89996 -4.32463e-09 7.94831 0 7.99777C4.3331e-09 8.04734 0.00480748 8.09578 0.0139828 8.14266C0.045022 8.3559 0.144956 8.561 0.313786 8.72181L7.73272 15.7885Z"
      fill="#F2F0EB"
    />
  </svg>
)

/**
 * TrashIcon Icon - For Delete links
 * Used in Rituals
 */
export const TrashIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg viewBox="0 0 20 21" fill={color} className={className} aria-hidden="true" {...props}>
    <path
      d="M8 3.5H12C12 2.39543 11.1046 1.5 10 1.5C8.89543 1.5 8 2.39543 8 3.5ZM6.5 3.5C6.5 1.567 8.067 0 10 0C11.933 0 13.5 1.567 13.5 3.5H19.25C19.6642 3.5 20 3.83579 20 4.25C20 4.66421 19.6642 5 19.25 5H17.9309L16.7589 17.1112C16.5729 19.0334 14.9575 20.5 13.0263 20.5H6.97369C5.04254 20.5 3.42715 19.0334 3.24113 17.1112L2.06908 5H0.75C0.335786 5 0 4.66421 0 4.25C0 3.83579 0.335786 3.5 0.75 3.5H6.5ZM8.5 8.25C8.5 7.83579 8.16421 7.5 7.75 7.5C7.33579 7.5 7 7.83579 7 8.25V15.75C7 16.1642 7.33579 16.5 7.75 16.5C8.16421 16.5 8.5 16.1642 8.5 15.75V8.25ZM12.25 7.5C12.6642 7.5 13 7.83579 13 8.25V15.75C13 16.1642 12.6642 16.5 12.25 16.5C11.8358 16.5 11.5 16.1642 11.5 15.75V8.25C11.5 7.83579 11.8358 7.5 12.25 7.5ZM4.73416 16.9667C4.84577 18.12 5.815 19 6.97369 19H13.0263C14.185 19 15.1542 18.12 15.2658 16.9667L16.4239 5H3.57608L4.73416 16.9667Z"
      fill="#C86757"
    />
  </svg>
)

/**
 * Money Icon - For refund action in completed bookings
 * Used in SessionBookingsModal.tsx
 */
export const MoneyIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 18,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 20 13" fill="none" className={className} {...props}>
    <path
      d="M7.79167 2.75C6.27288 2.75 5.04167 3.98122 5.04167 5.5C5.04167 7.01878 6.27288 8.25 7.79167 8.25C9.31045 8.25 10.5417 7.01878 10.5417 5.5C10.5417 3.98122 9.31045 2.75 7.79167 2.75ZM6.41667 5.5C6.41667 4.74061 7.03228 4.125 7.79167 4.125C8.55106 4.125 9.16667 4.74061 9.16667 5.5C9.16667 6.25939 8.55106 6.875 7.79167 6.875C7.03228 6.875 6.41667 6.25939 6.41667 5.5ZM0 2.0625C0 0.923413 0.923413 0 2.0625 0H13.5208C14.6599 0 15.5833 0.923412 15.5833 2.0625V5.65944C15.1419 5.55517 14.6816 5.5 14.2083 5.5V4.125H13.5208C12.3817 4.125 11.4583 3.20159 11.4583 2.0625V1.375H4.125V2.0625C4.125 3.20159 3.20159 4.125 2.0625 4.125H1.375V6.875H2.0625C3.20159 6.875 4.125 7.79841 4.125 8.9375V9.625H8.53737C8.39578 10.0633 8.30359 10.5238 8.26737 11H2.0625C0.923413 11 0 10.0766 0 8.9375V2.0625ZM2.0625 1.375C1.6828 1.375 1.375 1.6828 1.375 2.0625V2.75H2.0625C2.4422 2.75 2.75 2.4422 2.75 2.0625V1.375H2.0625ZM14.2083 2.75V2.0625C14.2083 1.6828 13.9005 1.375 13.5208 1.375H12.8333V2.0625C12.8333 2.4422 13.1411 2.75 13.5208 2.75H14.2083ZM1.375 8.9375C1.375 9.3172 1.6828 9.625 2.0625 9.625H2.75V8.9375C2.75 8.55781 2.4422 8.25 2.0625 8.25H1.375V8.9375ZM2.20125 12.375H8.32008C8.39414 12.8546 8.52539 13.3154 8.70666 13.75H4.58335C3.56546 13.75 2.67674 13.197 2.20125 12.375ZM16.9584 6.1712C17.4635 6.43446 17.9261 6.76798 18.3334 7.15879V4.58335C18.3334 3.56546 17.7803 2.67674 16.9584 2.20125V6.1712ZM19.25 11.4583C19.25 14.2428 16.9928 16.5 14.2083 16.5C11.4239 16.5 9.16667 14.2428 9.16667 11.4583C9.16667 8.6739 11.4239 6.41667 14.2083 6.41667C16.9928 6.41667 19.25 8.6739 19.25 11.4583ZM12.6991 9.30091C12.5201 9.12192 12.2299 9.12192 12.0509 9.30091C11.8719 9.4799 11.8719 9.7701 12.0509 9.94909L13.5602 11.4583L12.0509 12.9676C11.8719 13.1466 11.8719 13.4368 12.0509 13.6158C12.2299 13.7947 12.5201 13.7947 12.6991 13.6158L14.2083 12.1065L15.7176 13.6158C15.8966 13.7947 16.1868 13.7947 16.3658 13.6158C16.5447 13.4368 16.5447 13.1466 16.3658 12.9676L14.8565 11.4583L16.3658 9.94909C16.5447 9.7701 16.5447 9.4799 16.3658 9.30091C16.1868 9.12192 15.8966 9.12192 15.7176 9.30091L14.2083 10.8102L12.6991 9.30091Z"
      fill={color}
    />
  </svg>
)

/**
 * Calendar Icon - For date display in Edit Booking Modal
 * Used in AdminEditBookingModal.tsx
 */
export const CalendarIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 24,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="2" />
    <path d="M3 9H21" stroke={color} strokeWidth="2" />
    <path d="M8 2V5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M16 2V5" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
)

/**
 * Clock Icon - For time display in Edit Booking Modal
 * Used in AdminEditBookingModal.tsx
 */
export const ClockIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 24,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" />
    <path
      d="M12 7V12L15 15"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Figma People Icon - For Cancel Session modal detail rows
 * Figma component: 2751:6717 (People / Outlined / Default)
 */
export const PeopleOutlinedIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 24,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 96 96" fill="none" className={className} {...props}>
    <path
      d="M22 32C22 26.4772 26.4772 22 32 22C37.5228 22 42 26.4772 42 32C42 37.5228 37.5228 42 32 42C26.4772 42 22 37.5228 22 32ZM32 16C23.1634 16 16 23.1634 16 32C16 40.8366 23.1634 48 32 48C40.8366 48 48 40.8366 48 32C48 23.1634 40.8366 16 32 16ZM62 36C62 32.6863 64.6863 30 68 30C71.3137 30 74 32.6863 74 36C74 39.3137 71.3137 42 68 42C64.6863 42 62 39.3137 62 36ZM68 24C61.3726 24 56 29.3726 56 36C56 42.6274 61.3726 48 68 48C74.6274 48 80 42.6274 80 36C80 29.3726 74.6274 24 68 24ZM56.9938 76.1508C59.8026 77.2927 63.4068 78 68.004 78C77.1331 78 82.3466 75.2107 85.1915 71.7682C86.572 70.0977 87.2746 68.4106 87.6306 67.1181C87.8085 66.4724 87.9014 65.9202 87.9501 65.508C87.9745 65.3014 87.9879 65.1285 87.9953 64.9948C87.999 64.9279 88.0011 64.8707 88.0024 64.8237L88.0036 64.7611L88.0039 64.7357L88.004 64.7245L88.004 64.7143C88.004 59.9015 84.1025 56 79.2897 56H56.7183C56.6086 56 56.4993 56.002 56.3906 56.006C57.9666 57.6504 59.1147 59.708 59.6561 62H79.2897C80.7759 62 81.9831 63.1944 82.0037 64.6757C82.0022 64.6984 81.9988 64.7422 81.9915 64.8045C81.9737 64.9548 81.9338 65.2062 81.8461 65.5247C81.6709 66.1608 81.311 67.0452 80.5664 67.9461C79.1613 69.6464 75.8749 72 68.004 72C64.0837 72 61.3007 71.4161 59.321 70.6199C58.8923 72.2205 58.1809 74.1514 56.9938 76.1508ZM17 56C12.0294 56 8 60.0294 8 65V66.0043L8.00001 66.0089L8.00006 66.0193L8.00027 66.0442L8.00141 66.1108C8.00257 66.1624 8.00467 66.2286 8.00839 66.3083C8.01582 66.4676 8.02978 66.6818 8.0559 66.943C8.10805 67.4645 8.20931 68.1799 8.4066 69.0254C8.79979 70.7105 9.58618 72.9616 11.1673 75.2204C14.4426 79.8994 20.6871 84 32 84C43.3129 84 49.5574 79.8994 52.8327 75.2204C54.4138 72.9616 55.2002 70.7105 55.5934 69.0254C55.7907 68.1799 55.892 67.4645 55.9441 66.943C55.9702 66.6818 55.9842 66.4676 55.9916 66.3083C55.9953 66.2286 55.9974 66.1624 55.9986 66.1108L55.9997 66.0442L55.9999 66.0193L56 66.0089L56 65C56 60.0294 51.9706 56 47 56H17ZM14.0019 66.0286L14 65.9786V65C14 63.3431 15.3431 62 17 62H47C48.6569 62 50 63.3431 50 65V65.9786L49.9981 66.0286C49.9953 66.089 49.9888 66.1971 49.9739 66.346C49.944 66.6449 49.8812 67.1014 49.7504 67.6621C49.4873 68.7895 48.9612 70.2884 47.9173 71.7796C45.9426 74.6006 41.6871 78 32 78C22.3129 78 18.0574 74.6006 16.0827 71.7796C15.0388 70.2884 14.5127 68.7895 14.2496 67.6621C14.1188 67.1014 14.056 66.6449 14.0261 66.346C14.0112 66.1971 14.0047 66.089 14.0019 66.0286Z"
      fill={color}
    />
  </svg>
)

/**
 * Figma Calendar Icon - For Cancel Session modal detail rows
 * Figma component: 2751:1847 (Calendar / Outlined / LTR)
 */
export const CalendarFilledIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 24,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 96 96" fill="none" className={className} {...props}>
    <path
      d="M71 12C78.1797 12 84 17.8203 84 25V71C84 78.1797 78.1797 84 71 84H25C17.8203 84 12 78.1797 12 71V25C12 17.8203 17.8203 12 25 12H71ZM78 34H18V71C18 74.866 21.134 78 25 78H71C74.866 78 78 74.866 78 71V34ZM31 58C33.7614 58 36 60.2386 36 63C36 65.7614 33.7614 68 31 68C28.2386 68 26 65.7614 26 63C26 60.2386 28.2386 58 31 58ZM48 58C50.7614 58 53 60.2386 53 63C53 65.7614 50.7614 68 48 68C45.2386 68 43 65.7614 43 63C43 60.2386 45.2386 58 48 58ZM31 42C33.7614 42 36 44.2386 36 47C36 49.7614 33.7614 52 31 52C28.2386 52 26 49.7614 26 47C26 44.2386 28.2386 42 31 42ZM48 42C50.7614 42 53 44.2386 53 47C53 49.7614 50.7614 52 48 52C45.2386 52 43 49.7614 43 47C43 44.2386 45.2386 42 48 42ZM65 42C67.7614 42 70 44.2386 70 47C70 49.7614 67.7614 52 65 52C62.2386 52 60 49.7614 60 47C60 44.2386 62.2386 42 65 42ZM71 18H25C21.134 18 18 21.134 18 25V28H78V25C78 21.134 74.866 18 71 18Z"
      fill={color}
    />
  </svg>
)

/**
 * Figma Money Icon - For Cancel Session modal detail rows
 * Figma component: 2751:6171 (Money / Outlined / Off)
 */
export const MoneyOutlinedIcon: React.FC<IconProps> = ({
  color = "#C86757",
  size = 24,
  className,
  ...props
}) => (
  <svg width={size} height={size} viewBox="0 0 96 96" fill="none" className={className} {...props}>
    <path
      d="M13.1213 8.87868C11.9497 7.70711 10.0503 7.70711 8.87868 8.87868C7.70711 10.0503 7.70711 11.9497 8.87868 13.1213L15.8324 20.075C11.4134 20.6475 8 24.4251 8 29V59C8 63.9706 12.0294 68 17 68H63.7574L69.7369 73.9796C69.493 73.9932 69.2474 74.0001 69.0001 74.0001H17.6055C19.6803 77.5869 23.5584 80.0001 28.0001 80.0001H69.0001C71.0384 80.0001 73.0017 79.6791 74.8423 79.085L82.8787 87.1213C84.0503 88.2929 85.9497 88.2929 87.1213 87.1213C88.2929 85.9497 88.2929 84.0503 87.1213 82.8787L13.1213 8.87868ZM57.7574 62H26V59C26 54.0294 21.9706 50 17 50H14V38H17C21.5749 38 25.3525 34.5866 25.925 30.1676L32.4674 36.71C30.9197 38.7308 30 41.258 30 44C30 50.6274 35.3726 56 42 56C44.742 56 47.2692 55.0803 49.29 53.5326L57.7574 62ZM36.7861 41.0288L44.9712 49.2139C44.0952 49.7141 43.081 50 42 50C38.6863 50 36 47.3137 36 44C36 42.919 36.2859 41.9048 36.7861 41.0288ZM14 29C14 27.3431 15.3431 26 17 26H20V29C20 30.6569 18.6569 32 17 32H14V29ZM17 62C15.3431 62 14 60.6569 14 59V56H17C18.6569 56 20 57.3431 20 59V62H17ZM67 50C65.7394 50 64.5394 50.2592 63.4502 50.7271L68.7231 56H70V57.2769L75.2729 62.5498C75.7408 61.4606 76 60.2606 76 59V29C76 24.0294 71.9706 20 67 20H32.7231L38.7231 26H58V29C58 33.9706 62.0294 38 67 38H70V50H67ZM70 29V32H67C65.3431 32 64 30.6569 64 29V26H67C68.6569 26 70 27.3431 70 29ZM80.2471 67.524L84.5887 71.8656C86.7389 68.7864 88.0001 65.0405 88.0001 61.0001V40.0001C88.0001 35.5584 85.5869 31.6803 82.0001 29.6055V61.0001C82.0001 63.3778 81.3617 65.6064 80.2471 67.524Z"
      fill={color}
    />
  </svg>
)

/**
 * ReOrder Dots Icon — 6-dot drag handle from Figma (17x10)
 * Used in community media row layout for drag-to-reorder
 */
export const ReOrderDotsIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 17 10" fill={color} {...props}>
    <path d="M3 8.5C3 9.32843 2.32843 10 1.5 10C0.671574 10 0 9.32843 0 8.5C0 7.67157 0.671574 7 1.5 7C2.32843 7 3 7.67157 3 8.5ZM3 1.5C3 2.32843 2.32843 3 1.5 3C0.671574 3 0 2.32843 0 1.5C0 0.671573 0.671574 0 1.5 0C2.32843 0 3 0.671573 3 1.5ZM10 8.5C10 9.32843 9.32843 10 8.5 10C7.67157 10 7 9.32843 7 8.5C7 7.67157 7.67157 7 8.5 7C9.32843 7 10 7.67157 10 8.5ZM10 1.5C10 2.32843 9.32843 3 8.5 3C7.67157 3 7 2.32843 7 1.5C7 0.671573 7.67157 0 8.5 0C9.32843 0 10 0.671573 10 1.5ZM17 8.5C17 9.32843 16.3284 10 15.5 10C14.6716 10 14 9.32843 14 8.5C14 7.67157 14.6716 7 15.5 7C16.3284 7 17 7.67157 17 8.5ZM17 1.5C17 2.32843 16.3284 3 15.5 3C14.6716 3 14 2.32843 14 1.5C14 0.671573 14.6716 0 15.5 0C16.3284 0 17 0.671574 17 1.5Z" />
  </svg>
)

/**
 * Image Square Icon — landscape/photo icon from Figma (18x18)
 * Used in community media row for "set as cover" and thumbnail buttons
 */
export const ImageSquareIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 18 18" fill={color} {...props}>
    <path d="M14.75 0C16.5449 0 18 1.45507 18 3.25V14.75C18 16.5449 16.5449 18 14.75 18H3.25C1.45507 18 0 16.5449 0 14.75V3.25C0 1.45507 1.45507 0 3.25 0H14.75ZM15.3305 16.4014L9.52467 10.7148C9.25962 10.4553 8.85013 10.4316 8.55879 10.644L8.4752 10.7148L2.66845 16.4011C2.8504 16.4651 3.04613 16.5 3.25 16.5H14.75C14.9535 16.5 15.1489 16.4653 15.3305 16.4014ZM14.75 1.5H3.25C2.2835 1.5 1.5 2.2835 1.5 3.25V14.75C1.5 14.9584 1.53643 15.1583 1.60326 15.3437L7.42578 9.64299C8.25887 8.82729 9.56746 8.7885 10.4458 9.52658L10.5742 9.64312L16.3964 15.3447C16.4634 15.159 16.5 14.9588 16.5 14.75V3.25C16.5 2.2835 15.7165 1.5 14.75 1.5ZM12.2521 3.5C13.4959 3.5 14.5042 4.50831 14.5042 5.75212C14.5042 6.99592 13.4959 8.00423 12.2521 8.00423C11.0083 8.00423 10 6.99592 10 5.75212C10 4.50831 11.0083 3.5 12.2521 3.5ZM12.2521 5C11.8367 5 11.5 5.33673 11.5 5.75212C11.5 6.1675 11.8367 6.50423 12.2521 6.50423C12.6675 6.50423 13.0042 6.1675 13.0042 5.75212C13.0042 5.33673 12.6675 5 12.2521 5Z" />
  </svg>
)

/**
 * Pencil Edit Icon — from Figma (20x20)
 * Used in community media row for video title editing
 */
export const PencilEditIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 20 20" fill={color} {...props}>
    <path d="M18.9519 1.0481C17.5543 -0.349424 15.2885 -0.349357 13.8911 1.04825L1.94103 12.9997C1.5347 13.4061 1.2491 13.9172 1.116 14.4762L0.0204105 19.0777C-0.0399142 19.3311 0.0355221 19.5976 0.219685 19.7817C0.403848 19.9659 0.670367 20.0413 0.92373 19.981L5.52498 18.8855C6.08418 18.7523 6.59546 18.4666 7.00191 18.0601L18.952 6.10861C20.3493 4.71112 20.3493 2.4455 18.9519 1.0481ZM14.9518 2.10884C15.7634 1.29709 17.0795 1.29705 17.8912 2.10876C18.7028 2.9204 18.7029 4.23632 17.8913 5.04801L17 5.93946L14.0606 3.00012L14.9518 2.10884ZM13 4.06084L15.9394 7.00018L5.94119 16.9995C5.73104 17.2097 5.46668 17.3574 5.17755 17.4263L1.76191 18.2395L2.57521 14.8237C2.64402 14.5346 2.79168 14.2704 3.00175 14.0603L13 4.06084Z" />
  </svg>
)

/**
 * Trash Delete Icon — from Figma (20x20)
 * Used in community media row for delete action
 */
export const TrashDeleteIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 20 20" fill={color} {...props}>
    <path d="M8 3.5H12C12 2.39543 11.1046 1.5 10 1.5C8.89543 1.5 8 2.39543 8 3.5ZM6.5 3.5C6.5 1.567 8.067 0 10 0C11.933 0 13.5 1.567 13.5 3.5H19.25C19.6642 3.5 20 3.83579 20 4.25C20 4.66421 19.6642 5 19.25 5H17.9309L16.7589 17.1112C16.5729 19.0334 14.9575 20.5 13.0263 20.5H6.97369C5.04254 20.5 3.42715 19.0334 3.24113 17.1112L2.06908 5H0.75C0.335786 5 0 4.66421 0 4.25C0 3.83579 0.335786 3.5 0.75 3.5H6.5ZM8.5 8.25C8.5 7.83579 8.16421 7.5 7.75 7.5C7.33579 7.5 7 7.83579 7 8.25V15.75C7 16.1642 7.33579 16.5 7.75 16.5C8.16421 16.5 8.5 16.1642 8.5 15.75V8.25ZM12.25 7.5C12.6642 7.5 13 7.83579 13 8.25V15.75C13 16.1642 12.6642 16.5 12.25 16.5C11.8358 16.5 11.5 16.1642 11.5 15.75V8.25C11.5 7.83579 11.8358 7.5 12.25 7.5ZM4.73416 16.9667C4.84577 18.12 5.815 19 6.97369 19H13.0263C14.185 19 15.1542 18.12 15.2658 16.9667L16.4239 5H3.57608L4.73416 16.9667Z" />
  </svg>
)

/**
 * Play Triangle Icon — from Figma (17x18)
 * Used in community media row for video play overlay
 */
export const PlayTriangleIcon: React.FC<IconProps> = ({
  color = "currentColor",
  size = 20,
  className,
  ...props
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 17 18" fill={color} {...props}>
    <path d="M2.60846 1.59438C2.1087 1.32245 1.5 1.68422 1.5 2.25317V15.7055C1.5 16.2744 2.1087 16.6362 2.60846 16.3643L14.97 9.63811C15.4921 9.35406 15.4921 8.60459 14.97 8.32053L2.60846 1.59438ZM0 2.25317C0 0.546317 1.82609 -0.538993 3.32538 0.276796L15.687 7.00295C17.2531 7.85512 17.2531 10.1035 15.687 10.9557L3.32538 17.6819C1.82609 18.4976 0 17.4123 0 15.7055V2.25317Z" />
  </svg>
)

/**
 * Request Sent Icon — two-people group silhouette (19x16)
 * Used in community header to indicate a pending join request
 */
export const RequestSentIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 20,
  className,
  ...props
}) => (
  <svg className={className} width={size} height={size} viewBox="0 0 19 16" fill="none" {...props}>
    <path
      d="M3.20833 3.66667C3.20833 2.40101 4.23435 1.375 5.5 1.375C6.76565 1.375 7.79167 2.40101 7.79167 3.66667C7.79167 4.93232 6.76565 5.95833 5.5 5.95833C4.23435 5.95833 3.20833 4.93232 3.20833 3.66667ZM5.5 0C3.47496 0 1.83333 1.64162 1.83333 3.66667C1.83333 5.69171 3.47496 7.33333 5.5 7.33333C7.52504 7.33333 9.16667 5.69171 9.16667 3.66667C9.16667 1.64162 7.52504 0 5.5 0ZM12.375 4.58333C12.375 3.82394 12.9906 3.20833 13.75 3.20833C14.5094 3.20833 15.125 3.82394 15.125 4.58333C15.125 5.34272 14.5094 5.95833 13.75 5.95833C12.9906 5.95833 12.375 5.34272 12.375 4.58333ZM13.75 1.83333C12.2312 1.83333 11 3.06455 11 4.58333C11 6.10212 12.2312 7.33333 13.75 7.33333C15.2688 7.33333 16.5 6.10212 16.5 4.58333C16.5 3.06455 15.2688 1.83333 13.75 1.83333ZM11.2277 13.7846C11.8714 14.0462 12.6974 14.2083 13.7509 14.2083C15.843 14.2083 17.0378 13.5691 17.6897 12.7802C18.0061 12.3974 18.1671 12.0108 18.2487 11.7146C18.2894 11.5666 18.3107 11.44 18.3219 11.3456C18.3275 11.2982 18.3306 11.2586 18.3323 11.228C18.3331 11.2126 18.3336 11.1995 18.3339 11.1888L18.3342 11.1744L18.3342 11.1686L18.3342 11.166L18.3342 11.1637C18.3342 10.0608 17.4401 9.16667 16.3372 9.16667H11.1646C11.1395 9.16667 11.1144 9.16713 11.0895 9.16805C11.4507 9.54488 11.7138 10.0164 11.8379 10.5417H16.3372C16.6778 10.5417 16.9545 10.8154 16.9592 11.1549C16.9588 11.16 16.9581 11.1701 16.9564 11.1844C16.9523 11.2188 16.9432 11.2764 16.9231 11.3494C16.8829 11.4952 16.8004 11.6978 16.6298 11.9043C16.3078 12.294 15.5547 12.8333 13.7509 12.8333C12.8525 12.8333 12.2147 12.6995 11.7611 12.5171C11.6628 12.8839 11.4998 13.3264 11.2277 13.7846ZM2.0625 9.16667C0.923413 9.16667 0 10.0901 0 11.2292V11.4593L2.89579e-06 11.4604L1.27306e-05 11.4627L6.08116e-05 11.4685L0.000322089 11.4837C0.000589376 11.4956 0.00106969 11.5107 0.00192231 11.529C0.00362635 11.5655 0.00682473 11.6146 0.0128114 11.6744C0.0247606 11.7939 0.0479658 11.9579 0.0931795 12.1517C0.183285 12.5378 0.363499 13.0537 0.725841 13.5713C1.47644 14.6436 2.90747 15.5833 5.5 15.5833C8.09253 15.5833 9.52356 14.6436 10.2742 13.5713C10.6365 13.0537 10.8167 12.5378 10.9068 12.1517C10.952 11.9579 10.9752 11.7939 10.9872 11.6744C10.9932 11.6146 10.9964 11.5655 10.9981 11.529C10.9989 11.5107 10.9994 11.4956 10.9997 11.4837L10.9999 11.4685L11 11.4627L11 11.4604L11 11.2292C11 10.0901 10.0766 9.16667 8.9375 9.16667H2.0625ZM1.37543 11.4649L1.375 11.4534V11.2292C1.375 10.8495 1.6828 10.5417 2.0625 10.5417H8.9375C9.3172 10.5417 9.625 10.8495 9.625 11.2292V11.4534L9.62457 11.4649C9.62393 11.4787 9.62243 11.5035 9.61901 11.5376C9.61216 11.6061 9.59777 11.7107 9.56779 11.8392C9.5075 12.0976 9.38694 12.4411 9.14772 12.7828C8.69518 13.4293 7.71997 14.2083 5.5 14.2083C3.28003 14.2083 2.30481 13.4293 1.85228 12.7828C1.61306 12.4411 1.4925 12.0976 1.43221 11.8392C1.40223 11.7107 1.38784 11.6061 1.38099 11.5376C1.37757 11.5035 1.37607 11.4787 1.37543 11.4649Z"
      fill={color}
    />
  </svg>
)

/**
 * Bookmark Icon - For bookmarking/saving rituals and communities
 * Used in ritual cards, community cards, and various bookmark buttons
 */
export const BookmarkIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 13,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 13 18"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M1.08901 17.2835C0.634219 17.6107 0 17.2857 0 16.7254V2.97917C0 1.33382 1.33382 0 2.97917 0H9.85269C11.498 0 12.8319 1.33382 12.8319 2.97917V16.7254C12.8319 17.2857 12.1976 17.6107 11.7428 17.2835L6.41593 13.451L1.08901 17.2835ZM11.4569 2.97917C11.4569 2.09321 10.7386 1.375 9.85269 1.375H2.97917C2.09321 1.375 1.375 2.09321 1.375 2.97917V15.3839L6.01442 12.046C6.25428 11.8734 6.57758 11.8734 6.81744 12.046L11.4569 15.3839V2.97917Z"
      fill={color}
    />
  </svg>
)

/**
 * Bookmark Filled Icon - Filled version for saved/bookmarked state
 */
export const BookmarkFilledIcon: React.FC<IconProps> = ({
  color = "#F2F0EB",
  size = 13,
  className,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 13 18"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M1.08901 17.2835C0.634219 17.6107 0 17.2857 0 16.7254V2.97917C0 1.33382 1.33382 0 2.97917 0H9.85269C11.498 0 12.8319 1.33382 12.8319 2.97917V16.7254C12.8319 17.2857 12.1976 17.6107 11.7428 17.2835L6.41593 13.451L1.08901 17.2835Z"
      fill={color}
    />
  </svg>
)
